import React, { useEffect, useMemo, useState, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CurrencyDollarIcon,
  ChartBarIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { UserContext } from "../App";
import { db } from '../firebase';
import ZoomableImageModal from "../components/ZoomableImageModal";
import { doc, getDoc, setDoc, collection, onSnapshot } from 'firebase/firestore';
import Spinner from '../components/MatrixLoader';
import { useTheme } from '../contexts/ThemeContext';

import AnimatedNumber from '../components/AnimatedNumber';
import AutoFitText from '../components/AutoFitText';
import MoonHover from '../components/MoonHover';
import { Link, useNavigate } from "react-router-dom";
import { XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import {
  DEFAULT_BE_THRESHOLD,
  sanitizeBeThreshold,
  classifyTradeOutcomeByThreshold,
  getTradingPerformance,
} from '../statsUtils';
import { cleanCreatedTimestamp, getCreatedTimeMsFromCreated } from "../utils/dateUtils";
import { formatCurrencyCompact } from "../utils/numberFormat";
import { isTapeReading } from '../utils/tradeHelpers';
import { getLatestAccountBalance } from "../utils/accountBalanceUtils";
import {
  computeRecentForm,
  computeTradePace,
} from "../utils/performanceInsights";
import {
  getEvaluationProgress,
  getPropPhase,
  PROP_PHASE_EVALUATION,
} from "../utils/accountLifecycle";
import { getTodayJournalDestination } from "../utils/emptyStateUtils";
import {
  compareTradesChronologically,
  computeEquityDomain,
  computeEquityTicks,
  EQUITY_RANGE_CHIPS,
  getActiveEquityRange,
  getBestDayAndSession,
  getBestWorstTrades,
  getStreakBadge,
  getSummaryTileLinks,
  trimUnfundedLead,
} from "../utils/summaryStats";
import { useToast } from "../components/ui";

const SUMMARY_MODE_MIGRATION_VERSION = 1;
// Stat tiles that open a calendar view: native link, quiet hover, visible keyboard focus.
const SUMMARY_TILE_LINK_CLASS = 'cursor-pointer transition-colors duration-150 hover:bg-[var(--theme-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--theme-bg)]';
const DEFAULT_SUMMARY_SECTIONS = ['dayWeekMonth', 'equityCurve', 'tradingPerformance', 'keyStats', 'bestWorst'];

function sumPrecise(arr) {
  // Sums an array of numbers/strings as cents, returns float
  return arr.reduce((sum, v) => sum + Math.round(Number(v) * 100), 0) / 100;
}

// Helper function to extract entry date
function getEntryDate(entry) {
  if (entry.year && entry.month && entry.day) {
    const year = parseInt(entry.year, 10);
    const month = parseInt(entry.month, 10) - 1;
    const day = parseInt(entry.day, 10);
    return new Date(year, month, day);
  } else if (entry.created) {
    const lastDashIndex = entry.created.lastIndexOf('-');
    const cleanTimestamp = lastDashIndex > 10 ? entry.created.substring(0, lastDashIndex) : entry.created;
    return new Date(cleanTimestamp);
  }
  return null;
}

// Helper function to determine if an entry is an actual trade (not deposit/payout/tape/reset-excluded)
// Treats pnl as valid if it's a number OR a numeric string
function isTradeEntry(e) {
  if (e.isDeposit || e.isPayout || isTapeReading(e) || e.isResetExcluded) {
    return false;
  }
  // pnl can be a number or a numeric string
  const pnlValue = e.pnl;
  if (pnlValue === undefined || pnlValue === null || pnlValue === '') {
    return false;
  }
  const numericPnl = Number(pnlValue);
  return !isNaN(numericPnl);
}

// Helper function to get the time range for filtering based on selected time period
// Returns { start: Date, end: Date, active: boolean }
function getTimeRange(selectedTimePeriod, customStartDate = '', customEndDate = '') {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  
  if (selectedTimePeriod === 'all') {
    return { start: null, end: null, active: false };
  }
  
  if (selectedTimePeriod === '1month') {
    // Last 30 days rolling
    const start = new Date(today);
    start.setDate(start.getDate() - 30);
    start.setHours(0, 0, 0, 0); // Explicit normalization for consistency
    return { start, end: endOfToday, active: true };
  }
  
  if (selectedTimePeriod === '3months') {
    // Last 90 days rolling
    const start = new Date(today);
    start.setDate(start.getDate() - 90);
    start.setHours(0, 0, 0, 0); // Explicit normalization for consistency
    return { start, end: endOfToday, active: true };
  }
  
  if (selectedTimePeriod === 'ytd') {
    // January 1st of current year to today
    const start = new Date(now.getFullYear(), 0, 1);
    start.setHours(0, 0, 0, 0); // Explicit normalization for consistency
    return { start, end: endOfToday, active: true };
  }
  
  if (selectedTimePeriod === 'custom') {
    // Only active when both dates are set
    if (customStartDate && customEndDate) {
      // Parse YYYY-MM-DD strings explicitly to local time to avoid timezone shifts
      // new Date("YYYY-MM-DD") treats input as UTC, which can shift to previous day in Western timezones
      const [startYear, startMonth, startDay] = customStartDate.split('-').map(Number);
      const start = new Date(startYear, startMonth - 1, startDay);
      start.setHours(0, 0, 0, 0);
      
      const [endYear, endMonth, endDay] = customEndDate.split('-').map(Number);
      const end = new Date(endYear, endMonth - 1, endDay);
      end.setHours(23, 59, 59, 999);
      
      return { start, end, active: true };
    }
    // If dates aren't set, return inactive (show all-time)
    return { start: null, end: null, active: false };
  }
  
  // Default: no filtering
  return { start: null, end: null, active: false };
}

// Helper function to filter entries by a time range
function filterEntriesByRange(entries, range) {
  if (!range.active) {
    return entries;
  }
  
  return entries.filter(e => {
    const entryDate = getEntryDate(e);
    if (!entryDate || isNaN(entryDate.getTime())) {
      return false;
    }
    // Normalize entry date to start of day for comparison
    const normalizedEntryDate = new Date(entryDate);
    normalizedEntryDate.setHours(0, 0, 0, 0);
    return normalizedEntryDate >= range.start && normalizedEntryDate <= range.end;
  });
}

function getStats(entries, selectedTimePeriod = 'all', customStartDate = '', customEndDate = '', beThreshold = DEFAULT_BE_THRESHOLD) {
  // Return stats with zeros if no entries, rather than null
  // This allows the UI to show the stats section even when there's no data
  if (!entries.length) {
    return {
      totalPnl: 0,
      avgPnl: 0,
      avgDuration: 0,
      winRate: 0,
      avgWeekly: 0,
      avgMonthly: 0,
      avgRr: 0,
      wins: 0,
      losses: 0,
      breakeven: 0,
      totalTrades: 0,
      avgWinningTrade: 0,
      avgLosingTrade: 0,
      byMonth: {},
      byWeek: {},
    };
  }
  
  // Filter entries by time period using unified helper
  const range = getTimeRange(selectedTimePeriod, customStartDate, customEndDate);
  const filteredEntries = filterEntriesByRange(entries, range);
  const resolvedBeThreshold = sanitizeBeThreshold(beThreshold);
  
  // Only include entries that are actual trades using unified helper
  const tradeEntries = filteredEntries.filter(isTradeEntry);
  const totalPnl = sumPrecise(tradeEntries.map(e => e.pnl));
  const avgPnl = tradeEntries.length ? totalPnl / tradeEntries.length : 0;
  const avgDuration = tradeEntries.length ? tradeEntries.reduce((sum, e) => sum + (parseFloat(e.duration) || 0), 0) / tradeEntries.length : 0;
  let wins = 0;
  let losses = 0;
  let breakeven = 0;
  tradeEntries.forEach((entry) => {
    const outcome = classifyTradeOutcomeByThreshold(entry.pnl, resolvedBeThreshold);
    if (outcome === 'win') wins += 1;
    else if (outcome === 'loss') losses += 1;
    else breakeven += 1;
  });
  const decisiveTrades = wins + losses;
  const winRate = decisiveTrades ? (wins / decisiveTrades) * 100 : 0;
  const avgRr = tradeEntries.length ? sumPrecise(tradeEntries.map(e => e.rr)) / tradeEntries.length : 0;
  
  // Calculate average winning and losing trades
  const winningTrades = tradeEntries.filter(e => classifyTradeOutcomeByThreshold(e.pnl, resolvedBeThreshold) === 'win');
  const losingTrades = tradeEntries.filter(e => classifyTradeOutcomeByThreshold(e.pnl, resolvedBeThreshold) === 'loss');
  const avgWinningTrade = winningTrades.length ? sumPrecise(winningTrades.map(e => e.pnl)) / winningTrades.length : 0;
  const avgLosingTrade = losingTrades.length ? sumPrecise(losingTrades.map(e => e.pnl)) / losingTrades.length : 0;
  
  // Group by week/month for averages (ONLY TRADES, not deposits/payouts/reset-excluded)
  const byWeek = {};
  const byMonth = {};
  tradeEntries.forEach(e => {
    // Use year/month/day fields like the rest of the app
    let d;
    if (e.year && e.month && e.day) {
      const year = parseInt(e.year, 10);
      const month = parseInt(e.month, 10) - 1; // Convert to 0-indexed
      const day = parseInt(e.day, 10);
      d = new Date(year, month, day);
    } else {
      // Fallback to created timestamp
      d = new Date(e.created);
    }
    const week = `${d.getFullYear()}-W${getWeekNumber(d)}`;
    const month = `${d.getFullYear()}-${d.getMonth() + 1}`;
    const pnlValue = Number(e.pnl) || 0;
    byWeek[week] = Math.round(((byWeek[week] || 0) + pnlValue) * 100) / 100;
    byMonth[month] = Math.round(((byMonth[month] || 0) + pnlValue) * 100) / 100;
  });
  const avgWeekly = Object.keys(byWeek).length ? Object.values(byWeek).reduce((a, b) => a + b, 0) / Object.keys(byWeek).length : 0;
  const avgMonthly = Object.keys(byMonth).length ? Object.values(byMonth).reduce((a, b) => a + b, 0) / Object.keys(byMonth).length : 0;
  return {
    totalPnl,
    avgPnl,
    avgDuration,
    winRate,
    avgWeekly,
    avgMonthly,
    avgRr,
    wins,
    losses,
    breakeven,
    totalTrades: tradeEntries.length,
    avgWinningTrade,
    avgLosingTrade,
    byMonth,
    byWeek,
  };
}

function getEquityCurve(allEntries, selectedTimePeriod = 'all', customStartDate = '', customEndDate = '') {
  // Filter entries by time period using unified helper
  const range = getTimeRange(selectedTimePeriod, customStartDate, customEndDate);
  const filteredEntries = filterEntriesByRange(allEntries, range);
  
  // Helper function to get entry date string (YYYY-MM-DD format for grouping)
  const getDateString = (entry) => {
    if (entry.year && entry.month && entry.day) {
      const year = String(entry.year).padStart(4, '0');
      const month = String(entry.month).padStart(2, '0');
      const day = String(entry.day).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    if (entry.created) {
      const lastDashIndex = entry.created.lastIndexOf('-');
      if (lastDashIndex > 10) {
        const isoString = entry.created.substring(0, lastDashIndex);
        const date = new Date(isoString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    }
    return null;
  };

  // Count trades per day (using filtered entries)
  const tradesByDate = {};
  filteredEntries.forEach(entry => {
    if (!isTapeReading(entry) && !entry.isResetExcluded && !entry.isDeposit && !entry.isPayout) {
      const dateStr = getDateString(entry);
      if (dateStr) {
        tradesByDate[dateStr] = (tradesByDate[dateStr] || 0) + 1;
      }
    }
  });

  // Sort filtered entries chronologically - using created timestamp as primary sort
  const sorted = [...filteredEntries].sort((a, b) => {
    const getTimestamp = (entry) => {
      if (entry.created) {
        return getCreatedTimeMsFromCreated(entry.created);
      }
      if (entry.year && entry.month && entry.day) {
        const year = parseInt(entry.year, 10);
        const month = parseInt(entry.month, 10) - 1;
        const day = parseInt(entry.day, 10);
        return new Date(year, month, day).getTime();
      }
      return 0;
    };
    return getTimestamp(a) - getTimestamp(b);
  });
  
  // Calculate carry-in balance: sum of all balance-affecting entries BEFORE the range start
  // This ensures the equity curve starts at the true account balance at the beginning of the range
  let carryInBalance = 0;
  if (range.active && range.start) {
    // Get all entries before the range start
    const entriesBeforeRange = allEntries.filter(e => {
      const entryDate = getEntryDate(e);
      if (!entryDate || isNaN(entryDate.getTime())) return false;
      const normalizedDate = new Date(entryDate);
      normalizedDate.setHours(0, 0, 0, 0);
      return normalizedDate < range.start;
    });
    
    // Sort chronologically
    const sortedBefore = [...entriesBeforeRange].sort((a, b) => {
      const getTimestamp = (entry) => {
        if (entry.created) return getCreatedTimeMsFromCreated(entry.created);
        if (entry.year && entry.month && entry.day) {
          return new Date(parseInt(entry.year, 10), parseInt(entry.month, 10) - 1, parseInt(entry.day, 10)).getTime();
        }
        return 0;
      };
      return getTimestamp(a) - getTimestamp(b);
    });
    
    // Calculate carry-in balance from entries before range
    sortedBefore.forEach(e => {
      if (e.isBalanceAdjustment) {
        const targetBalance = Number(e.adjustmentTarget ?? e.accountBalance);
        carryInBalance = Number.isFinite(targetBalance)
          ? targetBalance
          : carryInBalance + (Number(e.pnl) || 0);
      } else if (e.isDeposit) {
        carryInBalance += Number(e.pnl) || 0;
      } else if (e.isPayout) {
        carryInBalance += Number(e.pnl) || 0; // Already negative
      } else if (!isTapeReading(e) && !e.isResetExcluded) {
        carryInBalance += Number(e.pnl) || 0;
      }
    });
    carryInBalance = Math.round(carryInBalance * 100) / 100;
  }
  
  let curve = [];
  let balance = carryInBalance; // Start from carry-in balance (0 for all-time, calculated for filtered ranges)
  let points = [];
  
  // Add initial point at carry-in balance
  const startLabel = range.active ? `Start (${range.start.toLocaleDateString()})` : 'Start';
  curve.push(balance);
  points.push({ x: 0, y: balance, date: startLabel, tradeCount: 0, accountBalance: balance, ts: range.active ? range.start.getTime() : null, hasTime: false });
  
  
  sorted.forEach((e, i) => {
    
    // Process each entry type and update balance
    let entryType = '';
    let displayPnl = 0;
    let shouldAddToCurve = false;
    
    if (e.isBalanceAdjustment) {
      const targetBalance = Number(e.adjustmentTarget ?? e.accountBalance);
      balance = Number.isFinite(targetBalance)
        ? targetBalance
        : balance + (Number(e.pnl) || 0);
      entryType = 'Balance Adjustment';
      displayPnl = Number(e.pnl) || 0;
      shouldAddToCurve = true;
    } else if (e.isDeposit) {
      const depositAmount = Number(e.pnl) || 0;
      balance += depositAmount;
      entryType = 'Deposit';
      displayPnl = depositAmount;
      shouldAddToCurve = true;
    } else if (e.isPayout) {
      const payoutAmount = Number(e.pnl) || 0; // This should already be negative
      balance += payoutAmount; // Adding a negative number subtracts it
      entryType = 'Payout';
      displayPnl = Math.abs(payoutAmount); // Show positive amount for display
      shouldAddToCurve = true;
          } else if (!isTapeReading(e) && !e.isResetExcluded) {
        const tradePnl = Number(e.pnl) || 0;
        balance += tradePnl;
        entryType = 'Trade';
        displayPnl = tradePnl;
        shouldAddToCurve = true;
      } else if (e.isResetExcluded) {
    } else {
    }
    
    // Round to avoid floating point precision issues
    balance = Math.round(balance * 100) / 100;
    
    // Add point to equity curve for each balance-affecting entry
    if (shouldAddToCurve) {
      curve.push(balance);
      
      // Create detailed point data
      const entryDate = e.created ? new Date(cleanCreatedTimestamp(e.created)) : (getEntryDate(e) || new Date());
      const dateLabel = entryDate.toLocaleDateString();
      const timeLabel = entryDate.toLocaleTimeString();
      const dateStr = getDateString(e);
      const tradeCount = dateStr ? (tradesByDate[dateStr] || 0) : 0;
      
      points.push({ 
        x: points.length, 
        y: balance, 
        date: dateLabel,
        time: timeLabel,
        type: entryType,
        pnl: displayPnl,
        ticker: e.tickerTraded || '',
        title: e.title || '',
        accountBalance: balance, // Explicitly store the account balance at this point
        tradeCount: tradeCount, // Number of trades on this day
        ts: entryDate.getTime(), // For the tooltip's readable date (display only)
        hasTime: Boolean(e.created),
      });
      
    }
  });
  
  
  return { curve, points };
}

function formatTradePnl(value) {
  const n = Number(value) || 0;
  return `${n > 0 ? '+' : n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getStreaks(entries, selectedTimePeriod = 'all', customStartDate = '', customEndDate = '') {
  // Filter entries by time period using unified helper
  const range = getTimeRange(selectedTimePeriod, customStartDate, customEndDate);
  const filteredEntries = filterEntriesByRange(entries, range);

  // Filter and sort trades by date to ensure chronological order
  const tradingEntries = filteredEntries
    .filter(isTradeEntry)
    .sort(compareTradesChronologically);

  let greenStreak = 0, lossStreak = 0, maxGreen = 0, maxLoss = 0;
  let prevWin = null;
  
  tradingEntries.forEach(e => {
    const pnl = Number(e.pnl);
    if (pnl > 0) {
      greenStreak++;
      lossStreak = 0;
      maxGreen = Math.max(maxGreen, greenStreak);
      prevWin = true;
    } else if (pnl < 0) {
      greenStreak = 0;
      lossStreak = prevWin === false ? lossStreak + 1 : 1;
      maxLoss = Math.max(maxLoss, lossStreak);
      prevWin = false;
    } else {
      greenStreak = 0;
      lossStreak = 0;
      prevWin = null;
    }
  });
  
  return { greenStreak, lossStreak, maxGreen, maxLoss };
}

function getDailyPnl(entries, selectedTimePeriod = 'all', customStartDate = '', customEndDate = '') {
  // Filter entries by time period using unified helper
  const range = getTimeRange(selectedTimePeriod, customStartDate, customEndDate);
  const filteredEntries = filterEntriesByRange(entries, range);
  
  const byDay = {};
  // Only count actual trades using unified helper
  filteredEntries.filter(isTradeEntry).forEach(e => {
    // Use year/month/day fields like the rest of the app
    if (e.year && e.month && e.day) {
      const year = parseInt(e.year, 10);
      const month = parseInt(e.month, 10) - 1; // Convert to 0-indexed
      const day = parseInt(e.day, 10);
      const date = new Date(year, month, day);
      const dayKey = date.toLocaleDateString();
      byDay[dayKey] = (byDay[dayKey] || 0) + (Number(e.pnl) || 0);
    } else {
      // Fallback to created timestamp
      const d = new Date(e.created);
      const day = d.toLocaleDateString();
      byDay[day] = (byDay[day] || 0) + (Number(e.pnl) || 0);
    }
  });
  return byDay;
}

function getWeekNumber(d) {
  // ISO week calculation - more reliable
  const date = new Date(d.getTime());
  date.setHours(0, 0, 0, 0);
  // Thursday in current week decides the year
  date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
  // January 4 is always in week 1
  const week1 = new Date(date.getFullYear(), 0, 4);
  // Adjust to Thursday in week 1 and count number of weeks from date to week1
  week1.setHours(0, 0, 0, 0);
  week1.setDate(week1.getDate() + 3 - (week1.getDay() + 6) % 7);
  const weekNo = 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
  return weekNo;
}

// Simple static number display - no animations - unused for now
// const StaticNumber = ({ value, decimals = 2, className = "" }) => {
//   return <span className={className}>{value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}</span>;
// };

// Helper function to parse created timestamp correctly
const parseCreatedDate = (entry) => {
  if (entry.created) {
    const lastDashIndex = entry.created.lastIndexOf('-');
    if (lastDashIndex > 10) { // Make sure it's not a date dash
      const isoString = entry.created.substring(0, lastDashIndex);
      return new Date(isoString);
    } else {
      return new Date(entry.created);
    }
  }
  // Fallback to constructed date from year/month/day fields
  if (entry.year && entry.month && entry.day) {
    return new Date(entry.year, entry.month - 1, entry.day);
  }
  return new Date(); // Last resort
};

// const iconClass = "w-6 h-6 inline-block mr-2 text-blue-400 align-middle"; // Unused for now

// Removed animation variants for minimalistic design

// Unused for now - kept for potential future use
// function formatDurationMins(mins) {
//   if (!mins || isNaN(mins)) return '';
//   const hours = Math.floor(mins / 60);
//   const minutes = Math.round(mins % 60);
//   if (hours > 0 && minutes > 0) return `${hours}hr ${minutes}min`;
//   if (hours > 0) return `${hours}hr`;
//   return `${minutes}min`;
// }

// EquityCurveChart component
const EQUITY_CHANGE_LABELS = {
  Trade: 'Trade P&L',
  Deposit: 'Deposit',
  Payout: 'Payout',
  'Balance Adjustment': 'Adjustment',
};

function formatBalance(value) {
  const n = Number(value) || 0;
  return `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatEquityPointDate(point) {
  if (!Number.isFinite(point?.ts)) return point?.date || '';
  const date = new Date(point.ts);
  const day = date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  return point.hasTime ? `${day} · ${date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}` : day;
}

// Custom Tooltip Component for Equity Curve: date, balance and what moved it.
const EquityCurveTooltip = ({ active, payload }) => {
  const { themeMeta } = useTheme();

  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload;
  const changeLabel = EQUITY_CHANGE_LABELS[point.type];
  // Payouts are stored as a positive display amount; show them as money leaving the account.
  const change = point.type === 'Payout' ? -Math.abs(Number(point.pnl) || 0) : Number(point.pnl) || 0;
  const muted = { color: themeMeta.tokens.textMuted };

  return (
    <div
      className="min-w-[190px] rounded-card px-3 py-2.5 text-sm shadow-elevated"
      style={{
        backgroundColor: themeMeta.tokens.chartTooltipBg,
        border: `1px solid ${themeMeta.tokens.border}`,
        color: themeMeta.tokens.text,
      }}
    >
      <div className="text-xs" style={muted}>
        {point.type
          ? formatEquityPointDate(point)
          : `${point.isNow ? 'Today' : 'Starting balance'}${Number.isFinite(point.ts) ? ` · ${formatEquityPointDate(point)}` : ''}`}
      </div>
      <div className="mt-0.5 text-base font-semibold tabular-nums">{formatBalance(point.accountBalance ?? point.y)}</div>
      {changeLabel && (
        <div className="mt-1.5 flex items-baseline justify-between gap-4 text-xs">
          <span style={muted}>
            {changeLabel}
            {point.type === 'Trade' && point.ticker ? ` · ${point.ticker}` : ''}
          </span>
          <span className={`font-semibold tabular-nums ${change > 0 ? 'theme-success' : change < 0 ? 'theme-loss' : ''}`}>
            {formatTradePnl(change)}
          </span>
        </div>
      )}
      {point.type === 'Trade' && point.tradeCount > 1 && (
        <div className="mt-1 flex justify-between gap-4 text-xs" style={muted}>
          <span>Trades that day</span>
          <span className="tabular-nums">{point.tradeCount}</span>
        </div>
      )}
    </div>
  );
};

// Dense curves read better as a plain line; dots stay on short histories.
const EQUITY_DOT_LIMIT = 40;

function EquityCurveChart({ points }) {
  const { themeMeta } = useTheme();
  
  if (!points || points.length < 2) {
    return (
      <div
        className="w-full h-80 flex items-center justify-center"
        style={{ color: themeMeta.tokens.textMuted }}
      >
        Not enough data for equity curve.
      </div>
    );
  }
  
  // Calculate Y-axis domain for better scaling
  // Real range with 10% padding (never clamped at 0) and round-number ticks.
  const yDomain = computeEquityDomain(points.map(p => p.y));
  const yTicks = computeEquityTicks(yDomain);
  
  const tickColor = themeMeta.tokens.textMuted;
  const strokeColor = themeMeta.tokens.chartPrimary;
  const dotFill = themeMeta.tokens.chartDot;
  const dotStroke = themeMeta.tokens.surface;
  const gradientStart = themeMeta.tokens.chartGradientStart;
  const gradientEnd = themeMeta.tokens.chartGradientEnd;
  const showDots = points.length <= EQUITY_DOT_LIMIT;
  
  return (
    <ResponsiveContainer width="100%" height={280}>
      <AreaChart data={points} margin={{ top: 20, right: 20, left: 4, bottom: 20 }}>
        <defs>
          <linearGradient id="equityGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={gradientStart} />
            <stop offset="100%" stopColor={gradientEnd} />
          </linearGradient>
        </defs>
        <XAxis 
          dataKey="x" 
          tick={{ fill: tickColor, fontSize: 11 }} 
          hide 
        />
        <YAxis 
          tick={{ fill: tickColor, fontSize: 11 }} 
          width={50} 
          domain={yDomain}
          ticks={yTicks.length ? yTicks : undefined}
          tickFormatter={(value) => {
            const abs = Math.abs(value);
            const body = abs >= 1000 ? `${(abs / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}k` : abs.toFixed(0);
            return `${value < 0 ? '-' : ''}$${body}`;
          }}
        />
        <Tooltip 
          content={<EquityCurveTooltip />}
          cursor={{ stroke: tickColor, strokeWidth: 1, strokeDasharray: '4 4' }}
          isAnimationActive={false}
        />
        <Area 
          type="monotone" 
          dataKey="y" 
          stroke={strokeColor} 
          fill="url(#equityGradient)" 
          strokeWidth={showDots ? 3 : 2.5} 
          dot={showDots ? { r: 4, fill: dotFill, stroke: dotStroke, strokeWidth: 2 } : false} 
          activeDot={{ r: 5, fill: strokeColor, stroke: dotStroke, strokeWidth: 2 }}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// Unused for now - kept for potential future use
// function getWeeksOfMonth(year, month) {
//   // Returns an array of [startDay, endDay] for each week in the month
//   const firstDay = new Date(year, month - 1, 1);
//   const lastDay = new Date(year, month, 0);
//   const weeks = [];
//   let current = new Date(firstDay);
//   while (current <= lastDay) {
//     const weekStart = new Date(current);
//     const weekEnd = new Date(current);
//     weekEnd.setDate(weekEnd.getDate() + (6 - weekEnd.getDay() + 1) % 7);
//     if (weekEnd > lastDay) weekEnd.setTime(lastDay.getTime());
//     weeks.push([new Date(weekStart), new Date(weekEnd)]);
//     current.setDate(current.getDate() + 7 - (current.getDay() + 6) % 7);
//   }
//   return weeks;
// }

// Unused for now - kept for potential future use
// function getWeeklyPnlsForMonth(entries, year, month) {
//   // Only entries in the given month that are actual trades (not deposits, payouts, or tape reading)
//   const filtered = entries.filter(e => 
//     String(e.year) === String(year) && 
//     String(e.month) === String(month) &&
//     !e.isDeposit && 
//     !e.isPayout && 
//     !e.isTapeReading &&
//     e.pnl !== undefined &&
//     e.pnl !== null &&
//     e.pnl !== ""
//   );
//   const daysInMonth = new Date(year, month, 0).getDate();
//   // Build a map day->PnL
//   const dayPnls = {};
//   filtered.forEach(e => {
//     const day = parseInt(e.day, 10);
//     if (!isNaN(day)) {
//       const pnlValue = Number(e.pnl) || 0;
//       dayPnls[day] = Math.round(((dayPnls[day] || 0) + pnlValue) * 100) / 100;
//     }
//   });
//   // Get week ranges
//   const weeks = [];
//   let weekStart = 1;
//   let weekEnd = 7 - (new Date(year, month - 1, 1).getDay() + 6) % 7;
//   if (weekEnd < 1) weekEnd = 7;
//   while (weekStart <= daysInMonth) {
//     if (weekEnd > daysInMonth) weekEnd = daysInMonth;
//     let sum = 0;
//     for (let d = weekStart; d <= weekEnd; d++) {
//       sum += dayPnls[d] || 0;
//     }
//     weeks.push({ weekStart, weekEnd, pnl: sum });
//     weekStart = weekEnd + 1;
//     weekEnd = weekStart + 6;
//   }
//   return weeks;
// }

function getPercentageChanges(entries, currentBalance, selectedTimePeriod = 'all', customStartDate = '', customEndDate = '') {
  if (!entries.length) return null;
  
  // Filter entries by time period using unified helper
  const range = getTimeRange(selectedTimePeriod, customStartDate, customEndDate);
  const filteredEntries = filterEntriesByRange(entries, range);
  
  // Sort all entries chronologically to calculate proper account balance progression
  const sortedEntries = [...filteredEntries].sort((a, b) => {
    const aTime = getCreatedTimeMsFromCreated(a.created);
    const bTime = getCreatedTimeMsFromCreated(b.created);
    return aTime - bTime;
  });
  
  // For "all time", calculate original capital from ALL entries (not just filtered)
  // For other periods, calculate from filtered entries only
  const entriesForCapital = selectedTimePeriod === 'all' ? entries : sortedEntries;
  
  // Calculate the starting capital (sum of all deposits minus payouts)
  const totalDeposits = sumPrecise(
    entriesForCapital
      .filter(e => e.isDeposit)
      .map(e => Number(e.pnl) || 0)
  );
  
  const totalPayouts = sumPrecise(
    entriesForCapital
      .filter(e => e.isPayout)
      .map(e => Number(e.pnl) || 0) // Already negative
  );
  
  // For percentage calculations, we need the ORIGINAL invested capital
  // This should be total deposits (don't subtract payouts for percentage base)
  // However, if there are multiple deposits/payouts, we need to be more careful
  let originalCapital = totalDeposits;
  
  // Safety check: If there are many deposits/payouts, warn the user
  const depositCount = entriesForCapital.filter(e => e.isDeposit).length;
  const payoutCount = entriesForCapital.filter(e => e.isPayout).length;
  
  // Handle different starting capital scenarios
  if (totalDeposits === 0 && totalPayouts === 0) {
    // Scenario 1: Brand new funded account (starting balance: $0)
    originalCapital = 0;
  } else if (totalDeposits === 0 && totalPayouts !== 0) {
    // Scenario 2: No deposits but payouts exist (manual balance edits)
    originalCapital = Math.abs(totalPayouts); // Use absolute value of payouts as starting capital
    console.warn('⚠️ FIXING: No deposits found but payouts exist. Using payout amount as starting capital.');
    console.warn(`   Original Capital set to: ${originalCapital} (based on payout: ${totalPayouts})`);
  }
  
  if (depositCount + payoutCount > 3) {
    console.warn('⚠️ WARNING: Multiple deposits/payouts detected. This may affect percentage calculations.');
    console.warn(`   Deposits: ${depositCount}, Payouts: ${payoutCount}`);
    console.warn('   Consider using the "Reset Account" feature for cleaner calculations.');
  }
  
  // Calculate total trading P&L (excluding deposits, payouts, and reset-excluded trades)
  // For "all time", use all entries; for other periods, use filtered entries
  const entriesForPnl = selectedTimePeriod === 'all' ? entries : sortedEntries;
  const totalTradingPnl = sumPrecise(
    entriesForPnl
      .filter(isTradeEntry)
      .map(e => Number(e.pnl) || 0)
  );
  
  // Overall account performance (trading performance only based on original capital)
  // For brand new accounts (starting with $0), show "New Account" instead of infinite%
  const overallPercentage = originalCapital > 0 ? (totalTradingPnl / originalCapital) * 100 : (totalTradingPnl > 0 ? 999999 : 0);
  
  // For day/week/month calculations, we'll show overall performance since 
  // the user specifically mentioned the issue with period-based calculations
  // Alternatively, we could calculate actual period-based performance if trades exist in those periods
  
  // Get the current date for period calculations
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const currentDay = now.getDate();
  
  // Helper function to get trades in a specific period
  const getTradesInPeriod = (startDate, endDate) => {
    return sortedEntries.filter(e => {
      if (e.isDeposit || e.isPayout || isTapeReading(e) || e.isResetExcluded) return false;
      const entryDate = getEntryDate(e);
      return entryDate >= startDate && entryDate <= endDate;
    });
  };
  
  // Calculate periods
  const startOfToday = new Date(currentYear, currentMonth - 1, currentDay, 0, 0, 0);
  const endOfToday = new Date(currentYear, currentMonth - 1, currentDay, 23, 59, 59);
  
  // Fix: Use Monday-start weeks to match calendar
  const startOfWeek = new Date(now);
  const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to Monday-start
  startOfWeek.setDate(now.getDate() - daysToMonday);
  startOfWeek.setHours(0, 0, 0, 0);
  
  const startOfMonth = new Date(currentYear, currentMonth - 1, 1);
  
  // Get trades for each period
  const todayTrades = getTradesInPeriod(startOfToday, endOfToday);
  const weekTrades = getTradesInPeriod(startOfWeek, now);
  const monthTrades = getTradesInPeriod(startOfMonth, now);
  
  // Calculate P&L for each period
  const dayPnl = sumPrecise(todayTrades.map(e => Number(e.pnl) || 0));
  const weekPnl = sumPrecise(weekTrades.map(e => Number(e.pnl) || 0));
  const monthPnl = sumPrecise(monthTrades.map(e => Number(e.pnl) || 0));
  
  // For percentage calculations, we have two options:
  // Option 1: Show 0% for periods with no trades (what user mentioned they want)
  // Option 2: Show overall performance in all cards
  // I'll implement Option 1 as per user's request
  
  // For brand new accounts (starting with $0), show high percentage for profitable periods
  const dayPercentage = todayTrades.length > 0 ? 
    (originalCapital > 0 ? (dayPnl / originalCapital) * 100 : (dayPnl > 0 ? 999999 : 0)) : 0;
  const weekPercentage = weekTrades.length > 0 ? 
    (originalCapital > 0 ? (weekPnl / originalCapital) * 100 : (weekPnl > 0 ? 999999 : 0)) : 0;
  const monthPercentage = monthTrades.length > 0 ? 
    (originalCapital > 0 ? (monthPnl / originalCapital) * 100 : (monthPnl > 0 ? 999999 : 0)) : 0;
  
  // Calculate custom period P&L if custom dates are set
  let customPeriod = null;
  if (selectedTimePeriod === 'custom' && customStartDate && customEndDate) {
    // Parse custom dates to local time
    const [startYear, startMonth, startDay] = customStartDate.split('-').map(Number);
    const customStart = new Date(startYear, startMonth - 1, startDay);
    customStart.setHours(0, 0, 0, 0);
    
    const [endYear, endMonth, endDay] = customEndDate.split('-').map(Number);
    const customEnd = new Date(endYear, endMonth - 1, endDay);
    customEnd.setHours(23, 59, 59, 999);
    
    // Get trades ONLY within the custom range from ALL entries (not pre-filtered)
    const customTrades = entries.filter(e => {
      if (e.isDeposit || e.isPayout || isTapeReading(e) || e.isResetExcluded) return false;
      const entryDate = getEntryDate(e);
      if (!entryDate || isNaN(entryDate.getTime())) return false;
      const normalizedDate = new Date(entryDate);
      normalizedDate.setHours(0, 0, 0, 0);
      return normalizedDate >= customStart && normalizedDate <= customEnd;
    });
    
    const customPnl = sumPrecise(customTrades.map(e => Number(e.pnl) || 0));
    
    // Use capital before first custom-range trade, with same fallbacks as overall.
    const allEntriesSorted = [...entries].sort((a, b) => {
      const aTime = getCreatedTimeMsFromCreated(a.created);
      const bTime = getCreatedTimeMsFromCreated(b.created);
      return aTime - bTime;
    });

    let runningCapital = 0;
    let capitalBeforeFirstCustomTrade = null;
    allEntriesSorted.forEach((entry) => {
      if (capitalBeforeFirstCustomTrade === null && isTradeEntry(entry)) {
        const entryDate = getEntryDate(entry);
        if (entryDate && !isNaN(entryDate.getTime())) {
          const normalizedDate = new Date(entryDate);
          normalizedDate.setHours(0, 0, 0, 0);
          if (normalizedDate >= customStart && normalizedDate <= customEnd) {
            capitalBeforeFirstCustomTrade = runningCapital;
          }
        }
      }

      if (entry.isBalanceAdjustment) {
        const targetBalance = Number(entry.adjustmentTarget ?? entry.accountBalance);
        runningCapital = Number.isFinite(targetBalance)
          ? targetBalance
          : runningCapital + (Number(entry.pnl) || 0);
      } else if (entry.isDeposit) {
        runningCapital += Number(entry.pnl) || 0;
      } else if (entry.isPayout) {
        runningCapital -= Math.abs(Number(entry.pnl) || 0);
      } else if (isTradeEntry(entry)) {
        runningCapital += Number(entry.pnl) || 0;
      }
    });

    const allTimeDeposits = sumPrecise(entries.filter(e => e.isDeposit).map(e => Number(e.pnl) || 0));
    const allTimePayouts = sumPrecise(entries.filter(e => e.isPayout).map(e => Number(e.pnl) || 0));

    let customCapitalBase = capitalBeforeFirstCustomTrade !== null ? capitalBeforeFirstCustomTrade : allTimeDeposits;
    if (customCapitalBase <= 0 && allTimeDeposits === 0 && allTimePayouts !== 0) {
      customCapitalBase = Math.abs(allTimePayouts);
    }

    if (customCapitalBase <= 0) {
      const firstTradeWithBalance = allEntriesSorted.find(
        (entry) =>
          isTradeEntry(entry) &&
          entry.accountBalance !== undefined &&
          entry.accountBalance !== null &&
          entry.accountBalance !== ""
      );
      if (firstTradeWithBalance) {
        const firstBalance = Number(firstTradeWithBalance.accountBalance) || 0;
        const firstPnl = Number(firstTradeWithBalance.pnl) || 0;
        const inferredCapital = firstBalance - firstPnl;
        if (inferredCapital > 0) {
          customCapitalBase = inferredCapital;
        }
      }
    }

    const customPercentage = customTrades.length > 0
      ? (customCapitalBase > 0 ? (customPnl / customCapitalBase) * 100 : (customPnl > 0 ? 999999 : 0))
      : 0;
    
    customPeriod = {
      pnl: customPnl,
      percentage: customPercentage,
      hasTrades: customTrades.length > 0,
      tradeCount: customTrades.length,
      startDate: customStartDate,
      endDate: customEndDate
    };
    
  }
  
  const result = {
    day: { 
      pnl: dayPnl, 
      percentage: dayPercentage,
      hasTrades: todayTrades.length > 0
    },
    week: { 
      pnl: weekPnl, 
      percentage: weekPercentage,
      hasTrades: weekTrades.length > 0
    },
    month: { 
      pnl: monthPnl, 
      percentage: monthPercentage,
      hasTrades: monthTrades.length > 0
    },
    overall: {
      pnl: totalTradingPnl,
      percentage: overallPercentage,
      startingCapital: originalCapital
    },
    custom: customPeriod
  };
  
  
  
  
  
  
  return result;
}


const SummaryPage = () => {
  const toast = useToast();
  const {
    currentUser,
    selectedAccount,
    dataRefreshTrigger,
    triggerDataRefresh,
    markEvaluationPassed,
  } = useContext(UserContext);
  const [stats, setStats] = useState(null);
  const [curveData, setCurveData] = useState({ curve: [], points: [] });
  const [streaks, setStreaks] = useState({});
  const [entries, setEntries] = useState([]);
  const [, setDailyPnl] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState('all'); // 'all', '1month', '3months', 'ytd', 'custom' - default to 'all'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [rulesStats, setRulesStats] = useState({ currentStreak: 0, totalYes: 0, totalNo: 0 });
  const [selectedTradeModal, setSelectedTradeModal] = useState(null);
  const [zoomImages, setZoomImages] = useState([]);
  const [zoomIndex, setZoomIndex] = useState(0);
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const navigate = useNavigate();
  const [goalPercentage, setGoalPercentage] = useState(20);
  const [beThreshold, setBeThreshold] = useState(DEFAULT_BE_THRESHOLD);
  const [summaryMode, setSummaryMode] = useState('advanced'); // 'basic' | 'advanced'
  const [summaryPreferencesLoaded, setSummaryPreferencesLoaded] = useState(false);
  const [markingEvaluationPassed, setMarkingEvaluationPassed] = useState(false);
  // Equity chart window. null = follow the page Time Period; a chip value overrides
  // it for the chart only and resets whenever the page period changes.
  const [equityRangeOverride, setEquityRangeOverride] = useState(null);

  useEffect(() => {
    setEquityRangeOverride(null);
  }, [selectedTimePeriod]);

  const openZoom = (images, index = 0) => {
    if (!images || images.length === 0) return;
    setZoomImages(images);
    setZoomIndex(index);
    setIsZoomOpen(true);
  };
  
  // Default section order
  const [sectionOrder, setSectionOrder] = useState(DEFAULT_SUMMARY_SECTIONS);
  
  // Time period dropdown options
  const timePeriodOptions = [
    { value: 'all', label: 'All Time' },
    { value: '1month', label: '1 Month' },
    { value: '3months', label: '3 Months' },
    { value: 'ytd', label: 'YTD' },
    { value: 'custom', label: 'Custom' }
  ];

  useEffect(() => {
    const fetchEntries = async () => {
      if (!currentUser || !selectedAccount) return;
      setLoading(true);
      try {
        const { db } = await import('../firebase');
        const { collection, getDocs } = await import('firebase/firestore');
        const entriesCol = collection(db, 'users', currentUser.uid, 'accounts', selectedAccount.id, 'entries');
        const snap = await getDocs(entriesCol);
        const data = snap.docs.map(doc => ({ ...doc.data(), id: doc.id }));
        
        
        setEntries(data);
        // For custom filter, use 'all' if dates aren't set
        const effectiveTimePeriod = (selectedTimePeriod === 'custom' && (!customStartDate || !customEndDate)) ? 'all' : selectedTimePeriod;
        const effectiveStartDate = (selectedTimePeriod === 'custom' && (!customStartDate || !customEndDate)) ? '' : customStartDate;
        const effectiveEndDate = (selectedTimePeriod === 'custom' && (!customStartDate || !customEndDate)) ? '' : customEndDate;
        
        const statsResult = getStats(data, effectiveTimePeriod, effectiveStartDate, effectiveEndDate, beThreshold);
        const curveResult = getEquityCurve(data, effectiveTimePeriod, effectiveStartDate, effectiveEndDate);
        
        
        setStats(statsResult);
        setCurveData(curveResult);
        setStreaks(getStreaks(data, effectiveTimePeriod, effectiveStartDate, effectiveEndDate));
        setDailyPnl(getDailyPnl(data, effectiveTimePeriod, effectiveStartDate, effectiveEndDate));
      } catch (error) {
        console.error('❌ SummaryPage: Error fetching entries:', error);
      }
      setLoading(false);
    };
    
    fetchEntries();
  }, [currentUser, selectedAccount, dataRefreshTrigger, selectedTimePeriod, customStartDate, customEndDate, beThreshold]);

  // Load section order and summaryMode from summaryLayout; time period from summaryFilters
  useEffect(() => {
    if (!currentUser) return;
    setSummaryPreferencesLoaded(false);
    
    const loadPreferences = async () => {
      try {
        const layoutRef = doc(db, 'users', currentUser.uid, 'preferences', 'summaryLayout');
        const layoutSnap = await getDoc(layoutRef);
        const layoutData = layoutSnap.exists() ? layoutSnap.data() : {};
        if (Array.isArray(layoutData.sectionOrder)) {
          const savedSections = layoutData.sectionOrder.filter((sectionId) => DEFAULT_SUMMARY_SECTIONS.includes(sectionId));
          const resolvedSections = [
            ...savedSections,
            ...DEFAULT_SUMMARY_SECTIONS.filter((sectionId) => !savedSections.includes(sectionId)),
          ];
          setSectionOrder(resolvedSections);
          if (JSON.stringify(resolvedSections) !== JSON.stringify(layoutData.sectionOrder)) {
            await setDoc(layoutRef, { sectionOrder: resolvedSections }, { merge: true });
          }
        }

        const hasCurrentModeMigration =
          layoutData.summaryModeMigrationVersion === SUMMARY_MODE_MIGRATION_VERSION;
        const savedMode = layoutData.summaryMode === 'basic' || layoutData.summaryMode === 'advanced'
          ? layoutData.summaryMode
          : 'advanced';
        const resolvedMode = hasCurrentModeMigration ? savedMode : 'advanced';
        setSummaryMode(resolvedMode);

        if (!hasCurrentModeMigration) {
          await setDoc(layoutRef, {
            summaryMode: 'advanced',
            summaryModeMigrationVersion: SUMMARY_MODE_MIGRATION_VERSION,
          }, { merge: true });
        }

        const filtersRef = doc(db, 'users', currentUser.uid, 'preferences', 'summaryFilters');
        const filtersSnap = await getDoc(filtersRef);
        if (filtersSnap.exists() && filtersSnap.data().selectedTimePeriod) {
          setSelectedTimePeriod(filtersSnap.data().selectedTimePeriod);
        }
      } catch (error) {
        console.error('Error loading preferences:', error);
      } finally {
        setSummaryPreferencesLoaded(true);
      }
    };
    
    loadPreferences();
  }, [currentUser]);

  // Save selectedTimePeriod when it changes
  useEffect(() => {
    if (!currentUser || !selectedTimePeriod || !summaryPreferencesLoaded) return;
    
    const saveSelectedTimePeriod = async () => {
      try {
        const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'summaryFilters');
        await setDoc(prefsRef, { selectedTimePeriod }, { merge: true });
      } catch (error) {
        console.error('Error saving selected time period:', error);
      }
    };
    
    saveSelectedTimePeriod();
  }, [currentUser, selectedTimePeriod, summaryPreferencesLoaded]);

  // Save summaryMode to summaryLayout when it changes
  useEffect(() => {
    if (!currentUser || !summaryMode || !summaryPreferencesLoaded) return;
    
    const saveSummaryMode = async () => {
      try {
        const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'summaryLayout');
        await setDoc(prefsRef, {
          summaryMode,
          summaryModeMigrationVersion: SUMMARY_MODE_MIGRATION_VERSION,
        }, { merge: true });
      } catch (error) {
        console.error('Error saving summary mode:', error);
      }
    };
    
    saveSummaryMode();
  }, [currentUser, summaryMode, summaryPreferencesLoaded]);

  // Load account goal and breakeven threshold from settings.
  useEffect(() => {
    if (!currentUser) return;
    const loadSettings = async () => {
      try {
        const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
        const prefsSnap = await getDoc(prefsRef);
        
        if (prefsSnap.exists()) {
          const data = prefsSnap.data();
          if (data.goalPercentage !== undefined) {
            setGoalPercentage(data.goalPercentage);
          }
          setBeThreshold(sanitizeBeThreshold(data.beThreshold));
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    
    loadSettings();
  }, [currentUser]);



  // Fetch rules tracking stats in real time
  useEffect(() => {
    if (!currentUser) return;
    const rulesCol = collection(db, 'users', currentUser.uid, 'rulesTracking');
    const unsubscribe = onSnapshot(rulesCol, (rulesSnap) => {
      const rulesData = rulesSnap.docs
        .map((rulesDoc) => ({ date: rulesDoc.id, ...rulesDoc.data() }))
        .filter((rule) => rule.response === 'yes' || rule.response === 'no')
        .sort((a, b) => b.date.localeCompare(a.date)); // most recent first

      const totalYes = rulesData.filter(r => r.response === 'yes').length;
      const totalNo = rulesData.filter(r => r.response === 'no').length;

      // Consecutive logged entries from most recent until first non-yes
      let currentStreak = 0;
      for (const rule of rulesData) {
        if (rule.response === 'yes') {
          currentStreak++;
        } else {
          break;
        }
      }

      setRulesStats({
        currentStreak,
        totalYes,
        totalNo
      });
    }, (error) => {
      console.error('Error listening to rules stats:', error);
    });

    return () => unsubscribe();
  }, [currentUser, dataRefreshTrigger]);

  // Note: Removed visibility/focus refresh since we now have triggerDataRefresh mechanism



  const { points } = curveData; // curve unused, only points needed
  const activeEquityRange = getActiveEquityRange(selectedTimePeriod, equityRangeOverride);
  const chartPoints = useMemo(() => {
    const source = equityRangeOverride ? getEquityCurve(entries, equityRangeOverride).points : points;
    // A chip window with no activity is just the carry-in point; draw it flat to today.
    if (source.length === 1 && equityRangeOverride) {
      const start = source[0];
      return [start, { ...start, x: 1, date: 'Today', ts: Date.now(), hasTime: false, isNow: true }];
    }
    return trimUnfundedLead(source);
  }, [entries, equityRangeOverride, points]);
  const handleEquityRangeChange = (value) => {
    setEquityRangeOverride(value === selectedTimePeriod ? null : value);
  };
  // Unused for now - kept for potential future use
  // const minY = Math.min(...curve);
  // const maxY = Math.max(...curve);
  // const height = 320;
  // const width = 700;

  // Prepare weekly and monthly PnL tables - unused for now
  // const weeklyRows = stats ? Object.entries(stats.byWeek).sort((a, b) => b[0].localeCompare(a[0])) : [];
  // const monthlyRows = stats ? Object.entries(stats.byMonth).sort((a, b) => b[0].localeCompare(a[0])) : [];
  // const dailyRows = Object.entries(dailyPnl).sort((a, b) => new Date(b[0]) - new Date(a[0]));

  // Compute current account balance - ALWAYS use all entries (not filtered by time period)
  // This ensures the displayed "Account Balance" reflects the true current balance
  let currentBalance = "0.00";
  
  if (entries.length > 0) {
    // Sort ALL entries chronologically - using created timestamp as primary sort
    const sorted = [...entries].sort((a, b) => {
      const getTimestamp = (entry) => {
        if (entry.created) {
          const timestamp = cleanCreatedTimestamp(entry.created); // Remove random suffix
          return new Date(timestamp).getTime();
        }
        // Fallback to date fields
        if (entry.year && entry.month && entry.day) {
          const year = parseInt(entry.year, 10);
          const month = parseInt(entry.month, 10) - 1;
          const day = parseInt(entry.day, 10);
          return new Date(year, month, day).getTime();
        }
        return 0;
      };
      return getTimestamp(a) - getTimestamp(b);
    });
    
    let bal = 0;
    
    sorted.forEach((e, index) => {
      
      if (e.isBalanceAdjustment) {
        const targetBalance = Number(e.adjustmentTarget ?? e.accountBalance);
        bal = Number.isFinite(targetBalance) ? targetBalance : bal + (Number(e.pnl) || 0);
      } else if (e.isDeposit) {
        // For deposits, just add the pnl amount (don't use stored accountBalance as it might be wrong)
        bal += Number(e.pnl) || 0;
      } else if (e.isPayout) {
        // For payouts, subtract the amount (pnl should already be negative)
        bal += Number(e.pnl) || 0;
      } else if (!isTapeReading(e) && !e.isResetExcluded) {
        // For trades, add the P&L to the previous balance
        bal += Number(e.pnl) || 0;
      } else if (e.isResetExcluded) {
      } else {
      }
      // Tape reading entries don't affect balance
    });
    // Round to 2 decimal places to avoid floating point precision issues
    const finalBalance = Math.round(bal * 100) / 100;
    currentBalance = finalBalance.toLocaleString(undefined, { maximumFractionDigits: 2 });
  } else {
  }

  // Keep the displayed balance on the shared, cents-safe ledger path covered by regression tests.
  currentBalance = getLatestAccountBalance(entries).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });

  // Calculate percentage changes (trading only, excluding deposits/payouts)
  // For custom filter, use 'all' if dates aren't set
  const effectiveTimePeriod = (selectedTimePeriod === 'custom' && (!customStartDate || !customEndDate)) ? 'all' : selectedTimePeriod;
  const effectiveStartDate = (selectedTimePeriod === 'custom' && (!customStartDate || !customEndDate)) ? '' : customStartDate;
  const effectiveEndDate = (selectedTimePeriod === 'custom' && (!customStartDate || !customEndDate)) ? '' : customEndDate;
  const percentageChanges = getPercentageChanges(entries, currentBalance, effectiveTimePeriod, effectiveStartDate, effectiveEndDate);
  
  // Get current month's trading performance for the goal
  const now = new Date();
  const tileLinks = getSummaryTileLinks(now);
  // "This Week" only links once it has trades; its empty state carries its own Log Trade button.
  const weekHasTrades = Boolean(percentageChanges?.week?.hasTrades);
  const WeekTile = weekHasTrades ? Link : 'div';
  const weekTileProps = weekHasTrades ? { to: tileLinks.week } : {};
  let currentMonthPerformance = null;
  
  // Only show current month performance if we're viewing all time
  if (selectedTimePeriod === 'all') {
    currentMonthPerformance = getTradingPerformance(entries, now.getFullYear(), now.getMonth() + 1);
    // Calculate progress toward custom goal instead of hardcoded 20%
    if (currentMonthPerformance && goalPercentage > 0) {
      currentMonthPerformance.progressTowardGoal = Math.min(100, Math.max(0, (currentMonthPerformance.percentage / goalPercentage) * 100));
    } else if (currentMonthPerformance) {
      currentMonthPerformance.progressTowardGoal = 0;
    }
  }
  
  // Only consider actual trades (not deposits, payouts, tape reading, or reset-excluded) for best/worst/recent
  // Apply both trade filter and time period filter using unified helpers
  const componentRange = getTimeRange(selectedTimePeriod, customStartDate, customEndDate);
  const tradeEntries = filterEntriesByRange(entries, componentRange).filter(isTradeEntry);
  const allTradeEntries = entries.filter(isTradeEntry);
  const recentForm = computeRecentForm(tradeEntries, 5, beThreshold);
  const bestDayAndSession = getBestDayAndSession(tradeEntries, beThreshold);
  const streakBadge = getStreakBadge(streaks);
  const tradePace = computeTradePace(allTradeEntries, now);
  const documentedTrades = tradeEntries.filter((trade) =>
    (Array.isArray(trade.screenshots) && trade.screenshots.length > 0)
    || String(trade.notes || trade.note || trade.description || '').trim()
  ).length;
  const reviewCoverage = tradeEntries.length > 0 ? (documentedTrades / tradeEntries.length) * 100 : 0;
  const rulesTotal = rulesStats.totalYes + rulesStats.totalNo;
  const rulesCompliance = rulesTotal > 0 ? (rulesStats.totalYes / rulesTotal) * 100 : 0;
  const payoffRatio = Math.abs(stats?.avgLosingTrade || 0) > 0
    ? Math.abs((stats?.avgWinningTrade || 0) / stats.avgLosingTrade)
    : null;
  const weeklyFocus = rulesTotal > 0 && rulesCompliance < 80
    ? 'Execution focus: improve rule adherence before adding more size or frequency.'
    : recentForm.recent.count >= 3 && recentForm.recent.totalPnl < 0
      ? 'Review the notes and screenshots from the recent five before the next session.'
      : tradePace.status === 'Above normal'
        ? 'Trade pace is above your normal active day. Check that every entry still met your plan.'
        : 'Execution is stable. Keep documenting the conditions behind each trade.';

  // Final summary

  // Find the most recent day, week, and month - unused for now
  // const mostRecentDay = dailyRows.length > 0 ? dailyRows[0] : null;
  // const mostRecentWeek = weeklyRows.length > 0 ? weeklyRows[0] : null;
  // const mostRecentMonth = monthlyRows.length > 0 ? monthlyRows[0] : null;
  // Helper function to get entry date using year/month/day fields (consistent with other functions) - unused for now
  // const getEntryDate = (entry) => {
  //   if (entry.year && entry.month && entry.day) {
  //     const year = parseInt(entry.year, 10);
  //     const month = parseInt(entry.month, 10) - 1;
  //     const day = parseInt(entry.day, 10);
  //     return new Date(year, month, day);
  //   }
  //   if (entry.created) {
  //     const timestamp = entry.created.split('-')[0];
  //     return new Date(timestamp);
  //   }
  //   return new Date();
  // };

  // const sortedTrades = [...tradeEntries].sort((a, b) => getEntryDate(b) - getEntryDate(a)); // Unused for now
  const { bestTrade, worstTrade } = getBestWorstTrades(tradeEntries);
  // if (selectedYear === 'all' || String(currentYear) === String(selectedYear)) {
  //   weeklyPnls = getWeeklyPnlsForMonth(entries, currentYear, currentMonth);
  // }
  // Get monthly PnL for current month - unused for now
  // const monthlyPnl = Math.round(weeklyPnls.reduce((sum, w) => sum + w.pnl, 0) * 100) / 100;

  // Find the most recent week and month - unused for now
  // const mostRecentWeekKey = weeklyRows.length > 0 ? weeklyRows[0][0] : null;
  // const mostRecentMonthKey = monthlyRows.length > 0 ? monthlyRows[0][0] : null;
  // const mostRecentWeekPnl = mostRecentWeekKey ? stats.byWeek[mostRecentWeekKey] : 0; // Unused for now
  // const mostRecentMonthPnl = mostRecentMonthKey ? stats.byMonth[mostRecentMonthKey] : 0; // Unused for now

  const handleReset = async () => {
    if (!currentUser || !selectedAccount) return;
    
    
    try {
      const { db } = await import('../firebase');
      const { collection, getDocs, deleteDoc, doc, updateDoc } = await import('firebase/firestore');
      
      const entriesCol = collection(db, 'users', currentUser.uid, 'accounts', selectedAccount.id, 'entries');
      const snap = await getDocs(entriesCol);
      
      
      const entriesToDelete = [];
      const entriesToMarkReset = [];
      
      snap.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (data.isDeposit || data.isPayout || data.isBalanceAdjustment) {
          entriesToDelete.push(docSnap);
        } else if (!isTapeReading(data)) {
          // Mark trades as reset-excluded (but keep tape readings visible)
          entriesToMarkReset.push(docSnap);
        } else {
        }
      });
      
      
      // Delete deposits and payouts
      if (entriesToDelete.length > 0) {
        await Promise.all(entriesToDelete.map(docSnap => 
          deleteDoc(doc(db, 'users', currentUser.uid, 'accounts', selectedAccount.id, 'entries', docSnap.id))
        ));
      }
      
      // Mark trades as reset (exclude from account P&L but keep for TradesPage)
      if (entriesToMarkReset.length > 0) {
        await Promise.all(entriesToMarkReset.map(docSnap => 
          updateDoc(doc(db, 'users', currentUser.uid, 'accounts', selectedAccount.id, 'entries', docSnap.id), {
            isResetExcluded: true,
            resetDate: new Date().toISOString(),
            resetAccountName: selectedAccount.name
          })
        ));
      }
      
      
      // Force refresh all data
      if (triggerDataRefresh) {
        triggerDataRefresh();
      } else {
        console.error('❌ triggerDataRefresh not available, falling back to reload');
        window.location.reload();
      }
      
      // Give a small delay to ensure the refresh propagates
      setTimeout(() => {
      }, 500);
      
    } catch (error) {
      console.error('❌ Error during reset:', error);
      toast.error(`Reset failed: ${error.message}`);
    }
  };

  // Remove any button or link that navigates to or shows 'Edit Account' on the summary page.

  // Check if this is a new account that needs a deposit
  // Use allTimeBalance (not filtered currentBalance) to avoid false onboarding for filtered periods
  // A zero or negative balance is still an established account with history.
  // Only a truly empty ledger should be routed through first-deposit onboarding.
  const isNewAccount = entries.length === 0;
  
  const handleStartWithDeposit = () => {
    navigate('/account-activity', {
      state: { openForm: true, activityType: 'deposit' },
    });
  };

  const handleLogTradeToday = () => {
    const destination = getTodayJournalDestination('/summary');
    navigate(destination.path, { state: destination.state });
  };

  const evaluationProgress = getPropPhase(selectedAccount) === PROP_PHASE_EVALUATION
    ? getEvaluationProgress(selectedAccount, entries)
    : null;

  const handleMarkEvaluationPassed = async () => {
    if (!selectedAccount || !evaluationProgress?.targetReached || !markEvaluationPassed) return;
    setMarkingEvaluationPassed(true);
    try {
      await markEvaluationPassed(selectedAccount.id);
    } catch (error) {
      console.error('Could not mark evaluation as passed:', error);
      toast.error(error.message || 'Could not mark this evaluation as passed.');
    } finally {
      setMarkingEvaluationPassed(false);
    }
  };

  const [showResetModal, setShowResetModal] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");

  const handleResetClick = () => {
    setShowResetModal(true);
    setResetConfirmText("");
  };

  const handleResetConfirm = async () => {
    if (resetConfirmText !== "RESET") return;
    setShowResetModal(false);
    await handleReset();
    setResetConfirmText("");
  };

  return (
    <div className="page-shell animate-fadeIn">
      <div className="mb-5 flex flex-col gap-2">
        <div className="flex w-full flex-col items-start gap-2">
          <div className="flex w-full items-center gap-4 flex-wrap justify-start">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-[#374151] dark:text-[#D1D5DB]">View:</span>
              <div className="theme-border flex overflow-hidden rounded-lg border">
                <button
                  type="button"
                  onClick={() => setSummaryMode('basic')}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    summaryMode === 'basic'
                      ? 'theme-nav-item-active'
                      : 'theme-nav-item'
                  }`}
                >
                  Basic
                </button>
                <button
                  type="button"
                  onClick={() => setSummaryMode('advanced')}
                  className={`theme-border border-l px-3 py-1.5 text-sm font-medium transition-colors ${
                    summaryMode === 'advanced'
                      ? 'theme-nav-item-active'
                      : 'theme-nav-item'
                  }`}
                >
                  Advanced
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-[#374151] dark:text-[#D1D5DB]">Time Period:</span>
            <select
              className="theme-input rounded-card border px-3 py-1 text-sm shadow-subtle transition-all duration-200 focus:outline-none min-w-[120px]"
              value={selectedTimePeriod}
              onChange={e => setSelectedTimePeriod(e.target.value)}
            >
              {timePeriodOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {selectedTimePeriod !== 'custom' && stats && (
              <span className="text-xs text-[#6B7280] dark:text-[#9CA3AF]">
                ({stats.totalTrades} trades)
              </span>
            )}
            </div>
            <button
              type="button"
              onClick={handleResetClick}
              className="rounded-lg border border-[#FCA5A5] bg-white px-3 py-1.5 text-sm font-semibold text-[#B91C1C] transition-colors hover:bg-[#FEF2F2] dark:border-[#7F1D1D] dark:bg-[#1F2937] dark:text-[#FCA5A5] dark:hover:bg-[#450A0A]/40 sm:ml-auto"
            >
              Reset Account
            </button>
          </div>
          {selectedTimePeriod === 'custom' && (
            <div className="flex w-full flex-wrap items-center gap-2">
              <label className="text-xs text-[#374151] dark:text-[#D1D5DB]">From:</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => {
                  setCustomStartDate(e.target.value);
                  // Validate: if end date is set and before start date, clear it
                  if (customEndDate && e.target.value && new Date(e.target.value) > new Date(customEndDate)) {
                    setCustomEndDate('');
                  }
                }}
                className="theme-input rounded-card border px-3 py-1 text-sm shadow-subtle transition-all duration-200 focus:outline-none [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100 dark:[&::-webkit-calendar-picker-indicator]:invert"
              />
              <label className="text-xs text-[#374151] dark:text-[#D1D5DB]">To:</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => {
                  const newEndDate = e.target.value;
                  // Validate: end date must be after start date
                  if (customStartDate && newEndDate && new Date(newEndDate) < new Date(customStartDate)) {
                    return; // Don't update if invalid
                  }
                  setCustomEndDate(newEndDate);
                }}
                min={customStartDate || undefined}
                className="theme-input rounded-card border px-3 py-1 text-sm shadow-subtle transition-all duration-200 focus:outline-none [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100 dark:[&::-webkit-calendar-picker-indicator]:invert"
              />
              {customStartDate && customEndDate && stats && (
                <span className="text-xs text-[#6B7280] dark:text-[#9CA3AF]">
                  ({stats.totalTrades} trades)
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {!loading && evaluationProgress && (
        <section className="theme-panel theme-border mb-5 rounded-2xl border p-4 shadow-subtle sm:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="theme-accent-soft rounded-full px-2.5 py-1 text-xs font-semibold">
                  Prop evaluation
                </span>
                {selectedAccount?.evaluationStatus === 'passed' && (
                  <span className="theme-success-soft rounded-full px-2.5 py-1 text-xs font-semibold">
                    Passed
                  </span>
                )}
              </div>
              <h2 className="mt-2 text-lg font-semibold text-[#111827] dark:text-[#F9FAFB]">
                {selectedAccount?.name} target progress
              </h2>
              <p className="mt-1 text-sm text-[#6B7280] dark:text-[#9CA3AF]">
                Trading P&amp;L counts toward the target. Deposits, payouts, tape readings, and reset-excluded trades do not.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:min-w-[360px]">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-[#6B7280] dark:text-[#9CA3AF]">Profit</div>
                  <div className="text-xl font-bold text-[#111827] dark:text-[#F9FAFB]">
                    {formatCurrencyCompact(evaluationProgress.profit)}
                    <span className="ml-1 text-sm font-medium text-[#6B7280] dark:text-[#9CA3AF]">
                      / {formatCurrencyCompact(evaluationProgress.target)}
                    </span>
                  </div>
                </div>
                <div className="theme-accent text-right text-sm font-semibold">
                  {evaluationProgress.percent.toFixed(0)}%
                </div>
              </div>
              <div className="theme-panel-alt h-2 overflow-hidden rounded-full">
                <div
                  className={`h-full rounded-full transition-all ${evaluationProgress.targetReached ? 'theme-success-action' : ''}`}
                  style={{ width: `${evaluationProgress.percent}%`, backgroundColor: evaluationProgress.targetReached ? undefined : 'var(--theme-accent)' }}
                />
              </div>
              {selectedAccount?.evaluationStatus !== 'passed' && (
                <button
                  type="button"
                  onClick={handleMarkEvaluationPassed}
                  disabled={!evaluationProgress.targetReached || markingEvaluationPassed}
                  className="theme-success-action self-stretch rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:bg-[#D1D5DB] disabled:text-[#6B7280] dark:disabled:bg-[#374151] dark:disabled:text-[#9CA3AF]"
                  title={
                    evaluationProgress.targetReached
                      ? 'Mark this evaluation as passed and archive it'
                      : `${formatCurrencyCompact(evaluationProgress.remaining)} remains before this evaluation can be passed`
                  }
                >
                  {markingEvaluationPassed
                    ? 'Passing and archiving...'
                    : evaluationProgress.targetReached
                      ? 'Mark Passed & Archive'
                      : `${formatCurrencyCompact(evaluationProgress.remaining)} to target`}
                </button>
              )}
            </div>
          </div>
        </section>
      )}
      
      {loading ? (
        <Spinner />
      ) : isNewAccount ? (
        // New Account Onboarding - Deposit First
        <div className="w-full flex flex-col items-center justify-center min-h-[60vh]">
          <div className="max-w-2xl text-center space-y-8">
            {/* Welcome Message */}
            <div className="space-y-4">
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-blue-500 to-emerald-500 rounded-full flex items-center justify-center shadow-2xl">
                <CurrencyDollarIcon className="w-12 h-12 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-[#111827] dark:text-[#F9FAFB] mb-4">
                Welcome to Your Bandz Journal!
              </h1>
              <p className="text-xl text-[#374151] dark:text-gray-300 leading-relaxed">
                Let's get started by adding your initial deposit to track your trading progress.
              </p>
            </div>

            {/* Call to Action */}
            <div className="space-y-4">
              <button
                onClick={handleStartWithDeposit}
                className="btn-primary text-xl font-bold px-12 py-4 shadow-elevated hover:scale-105"
              >
                Add Initial Deposit
              </button>
              
              <p className="text-sm text-[#374151] dark:text-gray-300">
                You can always add more deposits, payouts, and trades later.
              </p>
            </div>

            {/* Features Preview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 text-center">
              <div className="card-base">
                <ChartBarIcon className="theme-accent mx-auto mb-3 h-8 w-8" />
                <h3 className="text-lg font-semibold text-[#111827] dark:text-[#F9FAFB] mb-2">Track Performance</h3>
                <p className="text-sm text-[#374151] dark:text-gray-300">Monitor your P&L, win rate, and trading statistics</p>
              </div>
              
              <div className="card-base">
                <CalendarIcon className="theme-success w-8 h-8 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-[#111827] dark:text-[#F9FAFB] mb-2">Daily Journal</h3>
                <p className="text-sm text-[#374151] dark:text-gray-300">Log trades, screenshots, and notes for each day</p>
              </div>
              
              <div className="card-base">
                <ArrowTrendingUpIcon className="theme-accent mx-auto mb-3 h-8 w-8" />
                <h3 className="text-lg font-semibold text-[#111827] dark:text-[#F9FAFB] mb-2">{goalPercentage}% Monthly Goal</h3>
                <p className="text-sm text-[#374151] dark:text-gray-300">Track progress toward your monthly targets</p>
              </div>
            </div>
          </div>
        </div>
      ) : !stats ? (
        <div className="text-[#6B7280] dark:text-[#9CA3AF]">No data yet.</div>
      ) : selectedTimePeriod !== 'all' && selectedTimePeriod !== 'custom' && selectedTimePeriod !== 'ytd' && stats && stats.totalTrades === 0 ? (
        <div className="w-full flex flex-col items-center justify-center min-h-[60vh]">
          <div className="max-w-2xl text-center space-y-6">
            <div className="w-20 h-20 mx-auto bg-[#F3F4F6] rounded-full flex items-center justify-center">
              <CalendarIcon className="w-10 h-10 text-[#6B7280] dark:text-[#9CA3AF]" />
            </div>
            <h2 className="text-2xl font-semibold text-[#111827] dark:text-[#F9FAFB]">No Data for Selected Period</h2>
            <p className="text-[#6B7280] dark:text-[#9CA3AF]">There are no trades recorded for the selected time period.</p>
            <button
              onClick={() => setSelectedTimePeriod('all')}
              className="btn-primary"
            >
              View All Time
            </button>
          </div>
        </div>
      ) : (
        <div className="w-full flex flex-col gap-6">
          {(summaryMode === 'basic' ? ['dayWeekMonth', 'equityCurve', 'headlineStats'] : sectionOrder).map((sectionId) => {
            if (sectionId === 'dayWeekMonth') {
              // Check if we're in custom period mode with valid dates
              const isCustomPeriodActive = selectedTimePeriod === 'custom' && customStartDate && customEndDate;
              const customData = percentageChanges?.custom;
              
              return (
                <div key={sectionId}>
                      {/* --- TOP PRIORITY SECTION: Day/Week/Month P&L + Account Balance --- */}
                      {isCustomPeriodActive && customData ? (
                        /* Custom Period View - Shows Selected Period stats instead of Today/Week/Month */
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
                          {/* Selected Period P&L */}
                          <MoonHover>
                            <div className="card-base flex flex-col items-center justify-center min-h-[180px] h-full">
                              <div className="text-sm font-normal mb-2 uppercase tracking-wider text-[#374151] dark:text-[#9CA3AF]">Selected Period P&L</div>
                              <div className={`font-bold ${customData.pnl > 0 ? 'theme-success' : customData.pnl < 0 ? 'theme-loss' : 'text-[#111827] dark:text-[#F9FAFB]'}`}>
                                <AnimatedNumber 
                                  value={customData.pnl}
                                  prefix={customData.pnl > 0 ? '+$' : '$'}
                                  duration={1.5}
                                  delay={0}
                                  format="currencyCompact"
                                  autoFit={{ minPx: 18, maxPx: 40 }}
                                  className="w-full"
                                />
                              </div>
                              <div className={`text-sm font-semibold ${customData.percentage > 0 ? 'theme-success' : customData.percentage < 0 ? 'theme-loss' : 'text-[#6B7280] dark:text-[#9CA3AF]'}`}>
                                {customData.percentage >= 999999 ? 'New Account' : `${customData.percentage > 0 ? '+' : ''}${customData.percentage.toFixed(2)}%`}
                              </div>
                              <div className="text-xs text-[#6B7280] dark:text-[#9CA3AF] mt-1">
                                {new Date(customStartDate + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - {new Date(customEndDate + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                              </div>
                            </div>
                          </MoonHover>
                          
                          {/* Trades in Period */}
                          <div className="card-base flex flex-col items-center justify-center min-h-[180px]">
                            <div className="text-sm font-normal mb-2 uppercase tracking-wider text-[#374151] dark:text-[#9CA3AF]">Trades in Period</div>
                            <div className="theme-accent font-bold">
                              <AutoFitText minPx={18} maxPx={40}>
                                {customData.tradeCount}
                              </AutoFitText>
                            </div>
                            <div className="text-sm font-semibold text-[#6B7280] dark:text-[#9CA3AF]">
                              {customData.tradeCount === 0 ? 'No trades recorded' : customData.tradeCount === 1 ? 'trade' : 'trades'}
                            </div>
                          </div>
                          
                          {/* Overall Performance (All Time) */}
                          <div className="card-base flex flex-col items-center justify-center min-h-[180px]">
                            <div className="text-sm font-normal mb-2 uppercase tracking-wider text-[#374151] dark:text-[#9CA3AF]">Overall Performance</div>
                            {percentageChanges ? (
                              <>
                                <div className={`font-bold ${percentageChanges.overall.pnl > 0 ? 'theme-success' : percentageChanges.overall.pnl < 0 ? 'theme-loss' : 'text-[#111827] dark:text-[#F9FAFB]'}`}>
                                  <AnimatedNumber 
                                    value={percentageChanges.overall.pnl}
                                    prefix={percentageChanges.overall.pnl > 0 ? '+$' : '$'}
                                    duration={1.5}
                                    delay={0}
                                    format="currencyCompact"
                                    autoFit={{ minPx: 18, maxPx: 40 }}
                                    className="w-full"
                                  />
                                </div>
                                <div className={`text-sm font-semibold ${percentageChanges.overall.percentage > 0 ? 'theme-success' : percentageChanges.overall.percentage < 0 ? 'theme-loss' : 'text-[#6B7280] dark:text-[#9CA3AF]'}`}>
                                  {percentageChanges.overall.percentage >= 999999 ? 'New Account' : `${percentageChanges.overall.percentage > 0 ? '+' : ''}${percentageChanges.overall.percentage.toFixed(2)}%`}
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="text-[#111827] dark:text-[#F9FAFB] font-bold">
                                  <AutoFitText minPx={18} maxPx={40}>
                                    {formatCurrencyCompact(0)}
                                  </AutoFitText>
                                </div>
                                <div className="text-sm font-semibold text-[#6B7280] dark:text-[#9CA3AF]">0.00%</div>
                              </>
                            )}
                            <div className="text-xs text-[#6B7280] dark:text-[#9CA3AF] mt-1">All Time</div>
                          </div>
                        </div>
                      ) : (
                        /* Default View - Today/Week/Month/Overall */
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 w-full">
            {/* Day P&L */}
            <MoonHover>
              <Link to={tileLinks.today} className={`card-base flex flex-col items-center justify-center min-h-[180px] h-full ${SUMMARY_TILE_LINK_CLASS}`}>
              <div className="mb-2 flex items-center gap-2">
                <span className="text-sm font-normal uppercase tracking-wider text-[#374151] dark:text-[#9CA3AF]">Today's P&L</span>
                {streakBadge && (
                  <span
                    className={`${streakBadge.tone === 'win' ? 'theme-success-soft' : 'theme-loss'} rounded-full px-1.5 py-0.5 text-[11px] font-semibold leading-none tabular-nums`}
                    style={streakBadge.tone === 'win' ? undefined : { backgroundColor: 'var(--theme-loss-soft)' }}
                    title={`Current streak: ${streakBadge.description}`}
                  >
                    {streakBadge.label}
                    <span className="sr-only"> ({streakBadge.description})</span>
                  </span>
                )}
              </div>
              <span className="sr-only">Open today&apos;s journal.</span>
              {percentageChanges ? (
                <>
                  <div className={`font-bold ${percentageChanges.day.pnl > 0 ? 'theme-success' : percentageChanges.day.pnl < 0 ? 'theme-loss' : 'text-[#111827] dark:text-[#F9FAFB]'}`}>
                    <AnimatedNumber 
                      value={percentageChanges.day.pnl}
                      prefix={percentageChanges.day.pnl > 0 ? '+$' : '$'}
                      duration={1.5}
                      delay={0}
                      format="currencyCompact"
                      autoFit={{ minPx: 18, maxPx: 40 }}
                      className="w-full"
                    />
                  </div>
                  <div className={`text-sm font-semibold ${percentageChanges.day.percentage > 0 ? 'theme-success' : percentageChanges.day.percentage < 0 ? 'theme-loss' : 'text-[#6B7280] dark:text-[#9CA3AF]'}`}>
                    {percentageChanges.day.percentage >= 999999 ? 'New Account' : `${percentageChanges.day.percentage > 0 ? '+' : ''}${percentageChanges.day.percentage.toFixed(2)}%`}
                  </div>
                </>
              ) : <div className="text-[#6B7280] dark:text-[#9CA3AF] text-sm">No trades today</div>}
            </Link>
            </MoonHover>
            
            {/* Week P&L (a link once there is something to review; the empty state has its own Log Trade button) */}
            <WeekTile
              {...weekTileProps}
              className={`card-base flex flex-col items-center justify-center min-h-[180px] ${weekHasTrades ? SUMMARY_TILE_LINK_CLASS : ''}`}
            >
              <div className="text-sm font-normal mb-2 uppercase tracking-wider text-[#374151] dark:text-[#9CA3AF]">This Week</div>
              {weekHasTrades ? (
                <>
                  <span className="sr-only">Open this week in the month calendar.</span>
                  <div className={`font-bold ${percentageChanges.week.pnl > 0 ? 'theme-success' : percentageChanges.week.pnl < 0 ? 'theme-loss' : 'text-[#111827] dark:text-[#F9FAFB]'}`}>
                    <AnimatedNumber 
                      value={percentageChanges.week.pnl}
                      prefix={percentageChanges.week.pnl > 0 ? '+$' : '$'}
                      duration={1.5}
                      delay={0.1}
                      format="currencyCompact"
                      autoFit={{ minPx: 18, maxPx: 40 }}
                      className="w-full"
                    />
                  </div>
                  <div className={`text-sm font-semibold ${percentageChanges.week.percentage > 0 ? 'theme-success' : percentageChanges.week.percentage < 0 ? 'theme-loss' : 'text-[#6B7280] dark:text-[#9CA3AF]'}`}>
                    {percentageChanges.week.percentage >= 999999 ? 'New Account' : `${percentageChanges.week.percentage > 0 ? '+' : ''}${percentageChanges.week.percentage.toFixed(2)}%`}
                  </div>
                </>
              ) : (
                <div className="flex max-w-[220px] flex-col items-center text-center">
                  <ChartBarIcon className="mb-2 h-6 w-6 text-[#3B82F6] dark:text-[#60A5FA]" />
                  <div className="text-sm font-semibold text-[#111827] dark:text-[#F9FAFB]">No trades recorded this week</div>
                  <p className="mt-1 text-xs leading-5 text-[#6B7280] dark:text-[#9CA3AF]">Log your next trade to start this week&apos;s review.</p>
                  <button
                    type="button"
                    onClick={handleLogTradeToday}
                    className="mt-3 rounded-lg bg-[#3B82F6] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2563EB]"
                  >
                    Log Trade
                  </button>
                </div>
              )}
            </WeekTile>
            
            {/* Month P&L */}
            <Link to={tileLinks.month} className={`card-base flex flex-col items-center justify-center relative min-h-[180px] ${SUMMARY_TILE_LINK_CLASS}`}>
              <div className="text-sm font-normal mb-2 uppercase tracking-wider text-[#374151] dark:text-[#9CA3AF]">This Month</div>
              <span className="sr-only">Open this month&apos;s calendar.</span>
              {percentageChanges ? (
                <>
                  <div className={`font-bold ${percentageChanges.month.pnl > 0 ? 'theme-success' : percentageChanges.month.pnl < 0 ? 'theme-loss' : 'text-[#111827] dark:text-[#F9FAFB]'}`}>
                    <AnimatedNumber 
                      value={percentageChanges.month.pnl}
                      prefix={percentageChanges.month.pnl > 0 ? '+$' : '$'}
                      duration={1.5}
                      delay={0.2}
                      format="currencyCompact"
                      autoFit={{ minPx: 18, maxPx: 40 }}
                      className="w-full"
                    />
                  </div>
                  <div className={`text-sm font-semibold ${percentageChanges.month.percentage > 0 ? 'theme-success' : percentageChanges.month.percentage < 0 ? 'theme-loss' : 'text-[#6B7280] dark:text-[#9CA3AF]'}`}>
                    {percentageChanges.month.percentage >= 999999 ? 'New Account' : `${percentageChanges.month.percentage > 0 ? '+' : ''}${percentageChanges.month.percentage.toFixed(2)}%`}
                  </div>
                  
                  {/* Goal Progress Bar */}
                  <div className="w-full mt-3">
                    <div className="flex justify-between items-center text-xs text-[#6B7280] dark:text-[#9CA3AF] mb-1">
                      <span>Goal: {goalPercentage}%</span>
                      <span>{currentMonthPerformance ? (currentMonthPerformance.progressTowardGoal || 0).toFixed(0) : 0}%</span>
                    </div>
                    <div className="w-full bg-[#E5E7EB] rounded-full h-2 overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 ease-out ${
                          currentMonthPerformance && currentMonthPerformance.percentage >= goalPercentage ? 'theme-success-action' :
                          currentMonthPerformance && currentMonthPerformance.percentage >= goalPercentage / 2 ? 'bg-[#F59E0B]' : ''
                        }`}
                        style={{ 
                          width: `${currentMonthPerformance ? (currentMonthPerformance.progressTowardGoal || 0) : 0}%`,
                          backgroundColor: currentMonthPerformance && currentMonthPerformance.percentage < goalPercentage / 2
                            ? 'var(--theme-accent)'
                            : undefined,
                        }}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="font-bold leading-tight tracking-tight text-[#111827] dark:text-[#F9FAFB]">
                    <AutoFitText minPx={18} maxPx={40}>
                      {formatCurrencyCompact(0)}
                    </AutoFitText>
                  </div>
                  <div className="text-sm font-semibold text-[#6B7280] dark:text-[#9CA3AF]">0.00%</div>
                  
                  {/* Goal Progress Bar - Empty State */}
                  <div className="w-full mt-3">
                    <div className="flex justify-between items-center text-xs text-[#6B7280] dark:text-[#9CA3AF] mb-1">
                      <span>Goal: {goalPercentage}%</span>
                      <span>0%</span>
                    </div>
                    <div className="w-full bg-[#E5E7EB] dark:bg-[#374151] rounded-full h-2">
                      <div className="h-full w-0 bg-gradient-to-r from-blue-400 to-blue-500 transition-all duration-500" />
                    </div>
                  </div>
                </>
              )}
              <div className="text-xs text-[#6B7280] dark:text-[#9CA3AF] mt-1">{now.toLocaleString('default', { month: 'short' })} {now.getFullYear()}</div>
            </Link>
            
            {/* Overall Performance */}
            <div className="card-base flex flex-col items-center justify-center min-h-[180px]">
              <div className="text-sm font-normal mb-2 uppercase tracking-wider text-[#374151] dark:text-[#9CA3AF]">Overall Performance</div>
              {percentageChanges ? (
                <>
                  <div className={`font-bold ${percentageChanges.overall.pnl > 0 ? 'theme-success' : percentageChanges.overall.pnl < 0 ? 'theme-loss' : 'text-[#111827] dark:text-[#F9FAFB]'}`}>
                    <AnimatedNumber 
                      value={percentageChanges.overall.pnl}
                      prefix={percentageChanges.overall.pnl > 0 ? '+$' : '$'}
                      duration={1.5}
                      delay={0}
                      format="currencyCompact"
                      autoFit={{ minPx: 18, maxPx: 40 }}
                      className="w-full"
                    />
                  </div>
                  <div className={`text-sm font-semibold ${percentageChanges.overall.percentage > 0 ? 'theme-success' : percentageChanges.overall.percentage < 0 ? 'theme-loss' : 'text-[#6B7280] dark:text-[#9CA3AF]'}`}>
                    {percentageChanges.overall.percentage >= 999999 ? 'New Account' : `${percentageChanges.overall.percentage > 0 ? '+' : ''}${percentageChanges.overall.percentage.toFixed(2)}%`}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-[#111827] dark:text-[#F9FAFB] font-bold">
                    <AutoFitText minPx={18} maxPx={40}>
                      {formatCurrencyCompact(0)}
                    </AutoFitText>
                  </div>
                  <div className="text-sm font-semibold text-[#6B7280] dark:text-[#9CA3AF]">0.00%</div>
                </>
              )}
              <div className="text-xs text-[#6B7280] dark:text-[#9CA3AF] mt-1">Trading Performance</div>
            </div>
                        </div>
                      )}
                    </div>
                  );
                }
                if (sectionId === 'equityCurve') {
                  return (
                    <div key={sectionId}>
                      {/* --- EQUITY CURVE SECTION --- */}
                      <div className="bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] shadow-subtle rounded-card p-6 flex flex-col gap-4">
            {/* Title, range chips, balance. On phones the chips drop below so title and balance keep one row. */}
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 mb-4">
                <span className="order-1 text-2xl font-semibold text-[#111827] dark:text-[#F9FAFB] tracking-tight">Equity Curve</span>
                <div
                  role="group"
                  aria-label="Equity curve range"
                  className="theme-border order-3 flex overflow-hidden rounded-lg border sm:order-2 sm:mr-auto"
                >
                  {EQUITY_RANGE_CHIPS.map((chip, index) => {
                    const isActive = activeEquityRange === chip.value;
                    return (
                      <button
                        key={chip.value}
                        type="button"
                        onClick={() => handleEquityRangeChange(chip.value)}
                        aria-pressed={isActive}
                        title={chip.title}
                        className={`${index > 0 ? 'theme-border border-l ' : ''}min-w-[2.5rem] px-2.5 py-1 text-xs font-medium tabular-nums transition-colors focus-visible:relative focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--theme-accent)] ${
                          isActive ? 'theme-nav-item-active' : 'theme-nav-item'
                        }`}
                      >
                        {chip.label}
                      </button>
                    );
                  })}
                </div>
              <div className="order-2 w-full text-left sm:order-3 sm:w-auto sm:text-right">
                <div className="text-sm font-normal text-[#6B7280] dark:text-[#9CA3AF] uppercase tracking-wider">Account Balance</div>
                <div className="flex items-center justify-start gap-2 sm:justify-end">
                  <div className="theme-success text-xl font-semibold">
                    <AnimatedNumber 
                      value={parseFloat(currentBalance.replace(/,/g, '')) || 0}
                      prefix="$"
                      duration={2}
                      delay={0}
                      format="currencyCompact"
                      autoFit={{ minPx: 14, maxPx: 20 }}
                      className="w-full"
                    />
                  </div>
                  <button
                    onClick={() => navigate('/edit-account')}
                    className="p-1 text-[#6B7280] dark:text-[#9CA3AF] hover:text-[var(--theme-success)] rounded-card hover:bg-[#F3F4F6] dark:hover:bg-[#374151] transition-colors"
                    title="Edit Account Balance"
                    aria-label="Edit account balance"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
            <div className="w-full h-72 flex items-center justify-center bg-white dark:bg-[#1F2937]">
              <EquityCurveChart points={chartPoints} />
            </div>
                      </div>
                    </div>
                  );
                }
                if (sectionId === 'headlineStats') {
                  return (
                    <div key={sectionId}>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full">
                        <div className="card-base flex flex-col items-center justify-center min-h-[100px]">
                          <div className="text-xs font-normal uppercase tracking-wider text-[#374151] dark:text-[#9CA3AF]">Total P&L</div>
                          <div className={`font-bold ${stats.totalPnl > 0 ? "theme-success" : stats.totalPnl < 0 ? "theme-loss" : "text-[#6B7280] dark:text-[#9CA3AF]"}`}>
                            <AutoFitText minPx={14} maxPx={24}>
                              {stats.totalPnl > 0 ? '+' : ''}{formatCurrencyCompact(stats.totalPnl, { prefix: "", decimals: 2 })}
                            </AutoFitText>
                          </div>
                        </div>
                        <div className="card-base flex flex-col items-center justify-center min-h-[100px]">
                          <div className="text-xs font-normal uppercase tracking-wider text-[#374151] dark:text-[#9CA3AF]">Win rate</div>
                          <div className="theme-accent font-bold">
                            <AutoFitText minPx={14} maxPx={24}>
                              {stats.winRate?.toFixed(1) ?? "0.0"}%
                            </AutoFitText>
                          </div>
                        </div>
                        <div className="card-base flex flex-col items-center justify-center min-h-[100px]">
                          <div className="text-xs font-normal uppercase tracking-wider text-[#374151] dark:text-[#9CA3AF]">Total Trades</div>
                          <div className="font-bold text-[#111827] dark:text-[#F9FAFB]">
                            <AutoFitText minPx={14} maxPx={24}>
                              {stats.totalTrades.toLocaleString()}
                            </AutoFitText>
                          </div>
                        </div>
                        <div className="card-base flex flex-col items-center justify-center min-h-[100px]">
                          <div className="text-xs font-normal uppercase tracking-wider text-[#374151] dark:text-[#9CA3AF]">Avg P&L</div>
                          <div className="font-bold text-[#111827] dark:text-[#F9FAFB]">
                            <AutoFitText minPx={14} maxPx={24}>
                              {formatCurrencyCompact(stats.avgPnl, { prefix: "", decimals: 2 })}
                            </AutoFitText>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                if (sectionId === 'tradingPerformance') {
                  return (
                    <section key={sectionId} className="theme-panel theme-border rounded-2xl border p-5 shadow-subtle sm:p-6">
                      <div className="mb-5 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                          <div className="theme-accent text-xs font-semibold uppercase tracking-[0.16em]">Recent form</div>
                          <h2 className="mt-1 text-xl font-semibold tracking-tight text-[#111827] dark:text-[#F9FAFB]">Your latest five trades</h2>
                        </div>
                        <div className="text-xs text-[#6B7280] dark:text-[#9CA3AF]">
                          {recentForm.hasComparison ? 'Compared with the previous five' : 'Comparison appears after ten trades'}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                        <div className="theme-panel-alt rounded-xl p-4">
                          <div className="text-xs uppercase tracking-wider text-[#6B7280] dark:text-[#9CA3AF]">Recent P&amp;L</div>
                          <div className={`mt-2 text-2xl font-bold ${recentForm.recent.totalPnl > 0 ? 'theme-success' : recentForm.recent.totalPnl < 0 ? 'theme-loss' : 'text-[#111827] dark:text-[#F9FAFB]'}`}>
                            {recentForm.recent.totalPnl > 0 ? '+' : ''}{formatCurrencyCompact(recentForm.recent.totalPnl, { prefix: '', decimals: 2 })}
                          </div>
                          {recentForm.hasComparison && (
                            <div className={`mt-1 text-xs ${recentForm.pnlChange > 0 ? 'theme-success' : recentForm.pnlChange < 0 ? 'theme-loss' : 'text-[#6B7280] dark:text-[#9CA3AF]'}`}>
                              {recentForm.pnlChange > 0 ? '+' : ''}{formatCurrencyCompact(recentForm.pnlChange, { prefix: '', decimals: 2 })} vs prior five
                            </div>
                          )}
                        </div>
                        <div className="theme-panel-alt rounded-xl p-4">
                          <div className="text-xs uppercase tracking-wider text-[#6B7280] dark:text-[#9CA3AF]">Recent win rate</div>
                          <div className="mt-2 text-2xl font-bold text-[#111827] dark:text-[#F9FAFB]">{recentForm.recent.winRate.toFixed(0)}%</div>
                          <div className="mt-1 text-xs text-[#6B7280] dark:text-[#9CA3AF]">{recentForm.recent.wins} wins · {recentForm.recent.losses} losses</div>
                        </div>
                        <div className="theme-panel-alt rounded-xl p-4">
                          <div className="text-xs uppercase tracking-wider text-[#6B7280] dark:text-[#9CA3AF]">Average winner</div>
                          <div className="theme-success mt-2 text-2xl font-bold">{formatCurrencyCompact(stats.avgWinningTrade || 0)}</div>
                          <div className="mt-1 text-xs text-[#6B7280] dark:text-[#9CA3AF]">Selected period</div>
                        </div>
                        <div className="theme-panel-alt rounded-xl p-4">
                          <div className="text-xs uppercase tracking-wider text-[#6B7280] dark:text-[#9CA3AF]">Average loser</div>
                          <div className="theme-loss mt-2 text-2xl font-bold">{formatCurrencyCompact(Math.abs(stats.avgLosingTrade || 0))}</div>
                          <div className="mt-1 text-xs text-[#6B7280] dark:text-[#9CA3AF]">{payoffRatio != null ? `${payoffRatio.toFixed(2)}x payoff ratio` : 'No losing trades'}</div>
                        </div>
                      </div>
                    </section>
                  );
                }
                if (sectionId === 'keyStats') {
                  return (
                    <section key={sectionId} className="grid grid-cols-1 gap-4 lg:grid-cols-[1.2fr_0.8fr]">
                      <div className="theme-panel theme-border rounded-2xl border p-5 shadow-subtle sm:p-6">
                        <div className="mb-5">
                          <div className="theme-accent text-xs font-semibold uppercase tracking-[0.16em]">Execution health</div>
                          <h2 className="mt-1 text-xl font-semibold tracking-tight text-[#111827] dark:text-[#F9FAFB]">Discipline and trade pace</h2>
                        </div>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                          <div className="theme-panel-alt theme-border rounded-xl border p-4">
                            <div className="text-xs uppercase tracking-wider text-[#6B7280] dark:text-[#9CA3AF]">Rules followed</div>
                            <div className="mt-2 text-2xl font-bold text-[#111827] dark:text-[#F9FAFB]">{rulesCompliance.toFixed(0)}%</div>
                            <div className="mt-1 flex items-center gap-3 text-xs text-[#6B7280] dark:text-[#9CA3AF]">
                              <span className="flex items-center gap-1"><CheckIcon className="theme-success h-3.5 w-3.5" />{rulesStats.totalYes}</span>
                              <span className="flex items-center gap-1"><XMarkIcon className="theme-loss h-3.5 w-3.5" />{rulesStats.totalNo}</span>
                            </div>
                          </div>
                          <div className="theme-panel-alt theme-border rounded-xl border p-4">
                            <div className="text-xs uppercase tracking-wider text-[#6B7280] dark:text-[#9CA3AF]">Rule streak</div>
                            <div className="mt-2 text-2xl font-bold text-[#111827] dark:text-[#F9FAFB]">{rulesStats.currentStreak}</div>
                            <div className="mt-1 text-xs text-[#6B7280] dark:text-[#9CA3AF]">consecutive compliant days</div>
                          </div>
                          <div className="theme-panel-alt theme-border rounded-xl border p-4">
                            <div className="text-xs uppercase tracking-wider text-[#6B7280] dark:text-[#9CA3AF]">Trades today</div>
                            <div className="mt-2 text-2xl font-bold text-[#111827] dark:text-[#F9FAFB]">{tradePace.today}</div>
                            <div className={`mt-1 text-xs ${tradePace.status === 'Above normal' ? 'text-[#D97706] dark:text-[#FBBF24]' : 'text-[#6B7280] dark:text-[#9CA3AF]'}`}>{tradePace.status}</div>
                          </div>
                          <div className="theme-panel-alt theme-border rounded-xl border p-4">
                            <div className="text-xs uppercase tracking-wider text-[#6B7280] dark:text-[#9CA3AF]">Typical active day</div>
                            <div className="mt-2 text-2xl font-bold text-[#111827] dark:text-[#F9FAFB]">{tradePace.typical.toFixed(1)}</div>
                            <div className="mt-1 text-xs text-[#6B7280] dark:text-[#9CA3AF]">trades per active day</div>
                          </div>
                        </div>
                      </div>
                      <div className="theme-panel theme-border rounded-2xl border p-5 shadow-subtle sm:p-6">
                        <div className="theme-accent text-xs font-semibold uppercase tracking-[0.16em]">This week&apos;s focus</div>
                        <p className="mt-3 text-base font-medium leading-relaxed text-[#111827] dark:text-[#F9FAFB]">{weeklyFocus}</p>
                        {(bestDayAndSession.day || bestDayAndSession.session) && (
                          <div className="mt-4 space-y-1 text-sm text-[#6B7280] dark:text-[#9CA3AF]" title="Highest average P&L per trade in the selected period, minimum 3 trades">
                            {bestDayAndSession.day && (
                              <div>
                                Best day: <span className="font-medium text-[#374151] dark:text-[#D1D5DB]">{bestDayAndSession.day.label}</span>
                                <span className="text-xs"> · {formatTradePnl(bestDayAndSession.day.expectancy)} avg, {bestDayAndSession.day.count} trades</span>
                              </div>
                            )}
                            {bestDayAndSession.session && (
                              <div>
                                Best session: <span className="font-medium text-[#374151] dark:text-[#D1D5DB]">{bestDayAndSession.session.displayLabel}</span>
                                <span className="text-xs"> · {formatTradePnl(bestDayAndSession.session.expectancy)} avg, {bestDayAndSession.session.count} trades</span>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="mt-5 border-t border-[#E5E7EB] pt-4 text-xs text-[#6B7280] dark:border-[#374151] dark:text-[#9CA3AF]">
                          Based on recent form, rules tracking, and today&apos;s trade pace.
                        </div>
                      </div>
                    </section>
                  );
                }
                if (sectionId === 'bestWorst') {
                  return (
                    <div key={sectionId}>
                      {/* --- STREAKS + BEST/WORST TRADES SECTION --- */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
            {/* Momentum */}
            <div className="theme-panel theme-border flex flex-col gap-4 rounded-card border p-6 shadow-subtle">
              <div>
                <div className="theme-accent text-xs font-semibold uppercase tracking-[0.16em]">Momentum</div>
                <div className="mt-1 text-xl font-semibold text-[#111827] dark:text-[#F9FAFB] tracking-tight">Current and longest streaks</div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="theme-success text-3xl font-semibold mb-1">{streaks.greenStreak}</div>
                  <div className="text-sm font-normal text-[#6B7280] dark:text-[#9CA3AF] uppercase tracking-wider">Current Green</div>
                </div>
                <div className="text-center">
                  <div className="theme-success text-3xl font-semibold mb-1">{streaks.maxGreen}</div>
                  <div className="text-sm font-normal text-[#6B7280] dark:text-[#9CA3AF] uppercase tracking-wider">Max Green</div>
                </div>
                <div className="text-center">
                  <div className="theme-loss text-3xl font-semibold mb-1">{streaks.lossStreak}</div>
                  <div className="text-sm font-normal text-[#6B7280] dark:text-[#9CA3AF] uppercase tracking-wider">Current Loss</div>
                </div>
                <div className="text-center">
                  <div className="theme-loss text-3xl font-semibold mb-1">{streaks.maxLoss}</div>
                  <div className="text-sm font-normal text-[#6B7280] dark:text-[#9CA3AF] uppercase tracking-wider">Max Loss</div>
                </div>
              </div>
              <div className="theme-panel-alt mt-auto rounded-xl p-4">
                <div className="text-xs uppercase tracking-wider text-[#6B7280] dark:text-[#9CA3AF]">Review coverage</div>
                <div className="mt-2 flex items-end justify-between gap-4">
                  <div>
                    <div className="text-2xl font-bold text-[#111827] dark:text-[#F9FAFB]">{reviewCoverage.toFixed(0)}%</div>
                    <div className="text-xs text-[#6B7280] dark:text-[#9CA3AF]">with notes or charts</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-[#111827] dark:text-[#F9FAFB]">{documentedTrades}/{tradeEntries.length}</div>
                    <div className="text-xs text-[#6B7280] dark:text-[#9CA3AF]">documented trades</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Best/Worst Trades */}
            <div className="flex flex-col gap-4">
              {bestTrade && (
                <div 
                  className="theme-panel theme-success-border border-l-4 border-r border-t border-b rounded-card p-4 flex flex-col gap-1.5 shadow-subtle cursor-pointer hover:shadow-elevated transition-all"
                  onClick={() => setSelectedTradeModal(bestTrade)}
                >
                  <div className="theme-success text-xs font-medium uppercase tracking-wider">Best Trade</div>
                  <div className="theme-success text-xl font-semibold leading-tight tracking-tight">{formatTradePnl(bestTrade.pnl)}</div>
                  <div className="text-xs text-[#6B7280] dark:text-[#9CA3AF]">{bestTrade.tickerTraded} • {parseCreatedDate(bestTrade).toLocaleDateString()}</div>
                  {bestTrade.screenshots && bestTrade.screenshots.length > 0 && (
                    <div className="theme-success-border w-full h-[150px] overflow-hidden rounded-card mt-1.5 border">
                      <img 
                        src={bestTrade.screenshots[0]} 
                        alt="Best trade chart" 
                        className="w-full h-full object-cover object-top cursor-zoom-in"
                        onClick={(e) => {
                          e.stopPropagation();
                          openZoom(bestTrade.screenshots, 0);
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
{worstTrade && (
                <div 
                  className="theme-panel theme-loss-border border-l-4 border-r border-t border-b rounded-card p-4 flex flex-col gap-1.5 shadow-subtle cursor-pointer hover:shadow-elevated transition-all"
                  onClick={() => setSelectedTradeModal(worstTrade)}
                >
                  <div className="theme-loss text-xs font-medium uppercase tracking-wider">Worst Trade</div>
                  <div className="theme-loss text-xl font-semibold leading-tight tracking-tight">{formatTradePnl(worstTrade.pnl)}</div>
                  <div className="text-xs text-[#6B7280] dark:text-[#9CA3AF]">{worstTrade.tickerTraded} • {parseCreatedDate(worstTrade).toLocaleDateString()}</div>
                  {worstTrade.screenshots && worstTrade.screenshots.length > 0 && (
                    <div className="theme-loss-border w-full h-[150px] overflow-hidden rounded-card mt-1.5 border">
                      <img 
                        src={worstTrade.screenshots[0]} 
                        alt="Worst trade chart" 
                        className="w-full h-full object-cover object-top cursor-zoom-in"
                        onClick={(e) => {
                          e.stopPropagation();
                          openZoom(worstTrade.screenshots, 0);
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
              {!worstTrade && tradeEntries.length > 0 && (
                <div className="theme-panel theme-success-border border-l-4 border-r border-t border-b rounded-card p-4 flex flex-col gap-1.5 shadow-subtle">
                  <div className="theme-success text-xs font-medium uppercase tracking-wider">Worst Trade</div>
                  <div className="theme-success text-xl font-semibold leading-tight tracking-tight">No losses this period</div>
                  <div className="text-xs text-[#6B7280] dark:text-[#9CA3AF]">All profitable trades</div>
                </div>
              )}
            </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })}

        </div>
      )}

      {/* Trade Detail Modal */}
      <AnimatePresence>
        {selectedTradeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setSelectedTradeModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-[#1F2937] rounded-card shadow-elevated max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-white dark:bg-[#1F2937] border-b border-[#E5E7EB] dark:border-[#374151] p-6 flex items-center justify-between z-10">
                <div>
                  <h2 className="text-2xl font-bold text-[#111827] dark:text-[#F9FAFB]">{selectedTradeModal.title || 'Trade Details'}</h2>
                  <div className="text-sm text-[#6B7280] dark:text-[#9CA3AF] mt-1">
                    {selectedTradeModal.tickerTraded} • {parseCreatedDate(selectedTradeModal).toLocaleDateString()}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedTradeModal(null)}
                  className="btn-icon text-[#6B7280] dark:text-[#9CA3AF] hover:text-[#111827] dark:text-[#F9FAFB]"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Screenshots */}
                {selectedTradeModal.screenshots && selectedTradeModal.screenshots.length > 0 && (
                  <div className="space-y-4">
                    {selectedTradeModal.screenshots.map((screenshot, idx) => (
                      <img
                        key={idx}
                        src={screenshot}
                        alt={`Trade chart ${idx + 1}`}
                        className="w-full rounded-card border border-[#E5E7EB] dark:border-[#374151] cursor-zoom-in"
                        onClick={() => openZoom(selectedTradeModal.screenshots, idx)}
                      />
                    ))}
                  </div>
                )}

                {/* Trade Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-medium text-[#374151] mb-1">P&L</div>
                    <div className={`text-xl font-bold ${selectedTradeModal.pnl >= 0 ? 'theme-success' : 'theme-loss'}`}>
                      {selectedTradeModal.pnl > 0 ? '+' : ''}{selectedTradeModal.pnl}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-[#374151] mb-1">R:R</div>
                    <div className="text-xl font-semibold text-[#111827] dark:text-[#F9FAFB]">{selectedTradeModal.rr || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-[#374151] mb-1">Entry Time</div>
                    <div className="text-base text-[#111827] dark:text-[#F9FAFB]">{selectedTradeModal.entryTime || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-[#374151] mb-1">Exit Time</div>
                    <div className="text-base text-[#111827] dark:text-[#F9FAFB]">{selectedTradeModal.exitTime || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-[#374151] mb-1">Duration</div>
                    <div className="text-base text-[#111827] dark:text-[#F9FAFB]">{selectedTradeModal.duration || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-[#374151] mb-1">Trade Session</div>
                    <div className="text-base text-[#111827] dark:text-[#F9FAFB]">{selectedTradeModal.tradeSession || 'N/A'}</div>
                  </div>
                </div>

                {/* Notes */}
                {selectedTradeModal.notes && (
                  <div>
                    <div className="text-sm font-medium text-[#374151] dark:text-[#D1D5DB] mb-2">Notes</div>
                    <div className="text-base text-[#111827] dark:text-[#F9FAFB] whitespace-pre-wrap bg-[#F9FAFB] dark:bg-[#1F2937] p-4 rounded-card border border-[#E5E7EB] dark:border-[#374151]">
                      {selectedTradeModal.notes}
                    </div>
                  </div>
                )}

                {/* View in Journal Button */}
                <div className="pt-4 border-t border-[#E5E7EB] dark:border-[#374151]">
                  <button
                    onClick={() => {
                      const entryId = selectedTradeModal.id || `${selectedTradeModal.created}-${selectedTradeModal.tickerTraded}`;
                      setSelectedTradeModal(null);
                      navigate(`/context?entryId=${encodeURIComponent(entryId)}`);
                    }}
                    className="btn-primary w-full"
                  >
                    View in Journal
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ZoomableImageModal
        isOpen={isZoomOpen}
        images={zoomImages}
        index={zoomIndex}
        onClose={() => setIsZoomOpen(false)}
        onPrev={() => setZoomIndex(prev => prev > 0 ? prev - 1 : zoomImages.length - 1)}
        onNext={() => setZoomIndex(prev => prev < zoomImages.length - 1 ? prev + 1 : 0)}
        showNavigation={zoomImages.length > 1}
      />

      {/* Reset Confirmation Modal */}
      {showResetModal && (
        <div 
          className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center"
          onClick={() => setShowResetModal(false)}
        >
          <div 
            className="bg-white dark:bg-[#1F2937] rounded-card p-6 max-w-md mx-4 shadow-elevated"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-[#111827] dark:text-[#F9FAFB] mb-4">Reset Entire Account?</h2>
            <p className="text-[#6B7280] dark:text-[#9CA3AF] mb-6">
              This will permanently delete all trades and reset balance to $0.
            </p>
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#374151] mb-2">
                Type "RESET" to confirm:
              </label>
              <input
                type="text"
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-3 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#EF4444] focus:border-[#EF4444] focus:outline-none transition-all"
                placeholder="RESET"
                autoFocus
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowResetModal(false);
                  setResetConfirmText("");
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleResetConfirm}
                disabled={resetConfirmText !== "RESET"}
                className={`${resetConfirmText === "RESET" ? "btn-destructive" : "btn-secondary opacity-50 cursor-not-allowed"}`}
              >
                Confirm Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SummaryPage; 
